import './style.css';
import { StampWorld } from './scene.js';
import { loadAssetCatalog, preloadGroup } from './assets.js';
import { sound } from './audio.js';
import {
  BUILDINGS, TROOPS, EXPANSIONS, CHARACTERS, RARITIES, SUMMON_ODDS,
  loadState, saveState, resetState, hydrateState, compactNumber, hallLevel,
  upgradeBuilding, upgradeCost, collectProduction, production, expandTerritory,
  trainTroop, applyBattleReward, buildBuilding, buildingCost, canAfford,
  activeCharacter, combatTroop, summonOne, summonMany, upgradeHero, grantTutorialReward,
  dailyMissionStatus, achievementStatus, claimDailyMission, claimDailyChest,
  claimAchievement, recordProgress, recommendedAction, storageCaps, moveBuilding,
  builderStatus, processConstruction, saveBaseLayout, loadBaseLayout, layoutSlotCount
} from './state.js';
import { GAME_RULES } from './rules.js';
import {
  CAMPAIGN_STAGES, EQUIPMENT_ITEMS, RESEARCH_NODES, beginCampaign, completeCampaign,
  completeDungeon, ensureAdventure, equipItem, researchNode, stageStatus, storeReplay, todayDungeon
} from './adventure.js';
import { STORE_PRODUCTS, COSMETIC_CATALOG, BillingService, applyVerifiedPurchase, purchaseCosmetic } from './monetization.js';
import { OnlineService } from './online.js';
import { AuthService } from './auth.js';
import { MultiplayerService } from './multiplayer.js';
import { Telemetry } from './telemetry.js';

const $ = selector => document.querySelector(selector);
const state = loadState();
const telemetry = new Telemetry(state.preferences?.analytics);
const haptic = pattern => { if (state.preferences?.haptics && navigator.vibrate) navigator.vibrate(pattern); };
let currentBuilding = null;
let selectedTroop = 'runner';
let battleDeadline = 0;
let battlePhase = 'scout';
let battleResolved = false;
let battleEnemy = null;
let toastTimer = 0;
let pendingTutorialAfterReveal = false;
let authMode = 'signin';
let activePanel = '';
let sheetReturnFocus = null;
let overlayReturnFocus = null;
let inviteJoinInFlight = false;
let rewardTimer = 0;
let achievementTimer = 0;
let tutorialFocusTimer = 0;
let tutorialGuided = false;
let lastBattleMarks = 0;
let pendingBattleReward = null;
let battleArmy = state.army;
let activeBattleType = '';
let battleDeploymentLog = [];
let battleStartedAt = 0;
let battleReplayMode = false;
let battleAdventure = null;
let replayTimers = [];
const announcedAchievements = new Set();
const billing = new BillingService();

const TUTORIAL = [
  { title: 'เกาะของเราเหลือเพียงฐานเดียว', text: 'นี่คือ Spirit Hall หัวใจของเมือง ลูมี่จะช่วยพาเมืองกลับมายิ่งใหญ่อีกครั้งทีละขั้น', action: 'เริ่มฟื้นฟู', reward: '🎁 ชุดทรัพยากรเริ่มต้น' },
  { title: 'สร้างรายได้ก้อนแรก', text: 'สร้าง Sun Mint เพื่อให้เมืองผลิตทองต่อเนื่อง อาคารทุกหลังจะเปลี่ยนรูปลักษณ์เมื่ออัปเกรด', action: 'เปิดร้านสร้าง', focus: '[data-panel="build"]', reward: 'รางวัล: 🪙 220 · 🪵 180' },
  { title: 'เก็บผลผลิต', text: 'ผลผลิตสะสมแม้ปิดเกม กดเก็บเพื่อรับทรัพยากรเข้าคลัง', action: 'เก็บตอนนี้', focus: '#collect-button', reward: 'รางวัล: 🪙 180 · 🪵 150' },
  { title: 'ตั้งค่ายกองทัพ', text: 'สร้าง Rally Hut เพื่อเปิดพื้นที่ฝึกนักรบและเพิ่มความจุกองทัพ', action: 'สร้าง Rally Hut', focus: '[data-panel="build"]', reward: 'รางวัล: 🪵 180 · 💎 60' },
  { title: 'เปิดประตู Spirit Gate', text: 'ใช้อัญเชิญฟรีครั้งแรก การเรียกนี้รับประกันตัวละครระดับ R เพื่อเป็นหัวหน้ากองชุดแรก', action: 'อัญเชิญฟรี', focus: '[data-panel="army"]', reward: 'รับประกัน R ขึ้นไป' },
  { title: 'ฝึกกำลังเสริม', text: 'นักรบแต่ละสายมีบทบาทตายตัว ส่วน Leader ที่เลือกจะเสริมพลังอย่างจำกัด ฝึกสายยิงเพิ่มอีกหนึ่งหน่วย', action: 'เปิดกองทัพ', focus: '[data-panel="army"]', reward: 'รางวัล: 🪵 260' },
  { title: 'ออกศึกครั้งแรก', text: 'เลือกนักรบแล้วแตะขอบเกาะเพื่อวางกำลัง ทำลาย 50% เพื่อรับดาวและรางวัล', action: 'เริ่มบุก', focus: '#battle-button', reward: 'รางวัลหลังศึก: ทรัพยากรชุดใหญ่' },
  { title: 'ยกระดับ Spirit Hall', text: 'อัปเกรดฐานหลักเป็นเลเวล 2 เพื่อปลดล็อกอาคารป้องกันและสิทธิ์ขยายเกาะ', action: 'เลือก Spirit Hall', reward: 'รางวัล: 🪙 900 · 🪵 700 · 💎 50' },
  { title: 'เปิดเกาะสายลม', text: 'สร้างสะพานไปยังพื้นที่ใหม่ แล้วเมืองจะมีที่วางฐานทัพและสิ่งปลูกสร้างมากขึ้น', action: 'เปิดแผนที่ขยาย', focus: '[data-panel="build"]', reward: 'ปลดล็อก Sky Dock' },
  { title: 'เมืองพร้อมเติบโตแล้ว!', text: 'จากนี้เลือกได้เองว่าจะเน้นสร้างฐาน สะสม UR จัดทัพ หรือบุกฐานเพื่อน ภารกิจทั้งหมดวนกลับมาเพิ่มพลังเมือง', action: 'เริ่มการผจญภัย', reward: '🎉 จบบทสอนบทที่ 1' }
];

const TUTORIAL_GUIDES = {
  1: { panel: 'build', text: 'เลือก Sun Mint แล้วกด “สร้าง” การ์ดที่สว่างอยู่คือเป้าหมายของขั้นนี้' },
  3: { panel: 'build', text: 'เลือก Rally Hut แล้วกด “สร้าง” เพื่อเปิดระบบฝึกกองทัพ' },
  4: { panel: 'summon', text: 'กด “อัญเชิญ 1” ที่สว่างอยู่ ครั้งแรกนี้ใช้ฟรีและรับประกันระดับ R' },
  5: { panel: 'army', text: 'กดฝึกนักรบสายยิง 1 หน่วยที่การ์ดเป้าหมาย' },
  7: { panel: '', text: 'กดปุ่มอัปเกรดบนการ์ด Spirit Hall ที่สว่างอยู่ด้านล่าง' },
  8: { panel: 'territory', text: 'กด “ขยาย” บนเกาะสายลม เพื่อสร้างสะพานไปพื้นที่ถัดไป' }
};

const world = new StampWorld($('#world'), {
  onBuildingSelected: building => selectBuilding(building),
  onBuildingMove: async (building, point) => {
    const result = moveBuilding(state, building.id, point.x, point.z, building.rotation || 0);
    if (!result.ok) {
      toast(result.reason);
      const root = world.worldRoot.children.find(child => child.userData?.state?.id === building.id);
      if (root) world.selectBuilding(root);
      return;
    }
    persist();
    await loadCity();
    toast(`ย้าย ${BUILDINGS[building.type].name} แล้ว`);
  },
  onBattleGround: point => deploySelected(point),
  onBattleUpdate: update => renderBattleUpdate(update),
  onBattleComplete: result => finishBattle(result),
  onSound: name => sound.play(name),
  onCombatImpact: impact => showCombatImpact(impact),
  onCinematic: message => showCinematic(message.kicker, message.title),
  onWorldMood: mood => $('#game-shell').classList.toggle('twilight', mood === 'twilight'),
  onQualityChange: level => telemetry.track('quality_auto_changed', { level })
});
world.setQuality(state.preferences?.quality || 'auto');

const online = new OnlineService(state, () => {
  persist(false);
  $('#online-dot').classList.toggle('online', online.online);
});
const auth = new AuthService();
const multiplayer = new MultiplayerService(auth, state, () => {
  const count = multiplayer.lobbyUsers.length;
  $('#online-count').textContent = count;
  $('#online-count').classList.toggle('hidden', count <= 0);
  if (activePanel === 'multiplayer') renderMultiplayerPanel();
});

function persist(sync = true) {
  processConstruction(state);
  saveState(state);
  renderResources();
  auth.queueSave(state);
  if (sync && online.online) {
    window.clearTimeout(persist.syncTimer);
    persist.syncTimer = window.setTimeout(() => online.sync().catch(() => {}), 1500);
  }
}

function authMessage(message, error = false) {
  const box = $('#auth-message');
  box.textContent = message;
  box.classList.remove('hidden');
  box.style.color = error ? '#ffb9a8' : '#ffe8aa';
}

function setAuthMode(mode) {
  authMode = mode;
  document.querySelectorAll('[data-auth-mode]').forEach(button => button.classList.toggle('active', button.dataset.authMode === mode));
  const recovery = mode === 'recovery';
  $('#auth-title').textContent = recovery ? 'ตั้งรหัสผ่านใหม่' : mode === 'signup' ? 'สร้างบัญชีผู้พิทักษ์' : 'กลับสู่เกาะของคุณ';
  $('#auth-copy').textContent = recovery ? 'ตั้งรหัสผ่านใหม่อย่างน้อย 8 ตัวอักษร แล้วระบบจะพากลับเข้าเมืองของคุณ' : mode === 'signup' ? 'เมืองปัจจุบันจะถูกผูกกับบัญชีใหม่และบันทึกบน Supabase' : 'ล็อกอินเพื่อโหลดเมือง ตัวละคร และความคืบหน้าข้ามอุปกรณ์';
  $('#auth-submit').textContent = recovery ? 'บันทึกรหัสผ่านใหม่' : mode === 'signup' ? 'สร้างบัญชีและบันทึกเมือง' : 'เข้าสู่เกาะ';
  $('#auth-password-label').childNodes[0].textContent = recovery ? 'รหัสผ่านใหม่' : 'รหัสผ่าน';
  $('#auth-password').autocomplete = mode === 'signin' ? 'current-password' : 'new-password';
  $('#auth-email-label').classList.toggle('hidden', recovery);
  $('#auth-email').required = !recovery;
  $('.auth-tabs').classList.toggle('hidden', recovery);
  $('#auth-forgot').classList.toggle('hidden', mode !== 'signin');
  $('.auth-divider').classList.toggle('hidden', recovery);
  $('#auth-guest').classList.toggle('hidden', recovery);
  $('#auth-message').classList.add('hidden');
}

function showAuthGate() {
  closeSheet();
  setAuthMode('signin');
  $('#auth-gate').classList.remove('hidden');
  window.requestAnimationFrame(() => $('#auth-email')?.focus());
}

async function attachAccount(reloadWorld = true) {
  const cloudState = await auth.loadOrCreate(state);
  if (cloudState) {
    Object.assign(state, hydrateState(cloudState));
    saveState(state);
    if (reloadWorld) await loadCity(true);
  } else {
    await auth.save(state);
  }
  localStorage.removeItem('clash-of-stamp-guest');
  renderResources();
}

function friendlyAuthError(error) {
  const message = String(error?.message || error || 'เข้าสู่ระบบไม่สำเร็จ');
  if (/invalid login credentials/i.test(message)) return 'อีเมลหรือรหัสผ่านไม่ถูกต้อง';
  if (/email not confirmed/i.test(message)) return 'กรุณายืนยันอีเมลก่อนเข้าสู่ระบบ';
  if (/already registered/i.test(message)) return 'อีเมลนี้มีบัญชีอยู่แล้ว ลองเข้าสู่ระบบแทน';
  if (/password/i.test(message) && /short|least|weak/i.test(message)) return 'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษรและคาดเดายาก';
  return message;
}

function toast(message, duration = 2600) {
  window.clearTimeout(toastTimer);
  $('#toast').textContent = message;
  $('#toast').classList.remove('hidden');
  toastTimer = window.setTimeout(() => $('#toast').classList.add('hidden'), duration);
}

function svgIcon(name) {
  return `<svg class="ui-icon" aria-hidden="true"><use href="#i-${name}"/></svg>`;
}

function showCombatImpact({ screen, damage, critical, destroyed, hostile }) {
  if (!screen) return;
  const floater = document.createElement('span');
  floater.className = `damage-number${critical ? ' critical' : ''}${destroyed ? ' destroyed' : ''}${hostile ? ' hostile' : ''}`;
  floater.style.left = `${screen.x}px`;
  floater.style.top = `${screen.y}px`;
  floater.textContent = destroyed ? 'DESTROYED' : `${critical ? 'CRIT ' : ''}${damage}`;
  $('#combat-floaters').appendChild(floater);
  window.setTimeout(() => floater.remove(), 800);
  if (critical || destroyed) {
    haptic(destroyed ? [28, 45, 38] : 18);
    const flash = $('#screen-flash');
    flash.className = `screen-flash active${hostile ? ' danger' : ''}`;
    window.setTimeout(() => { flash.className = 'screen-flash'; }, 340);
    $('#game-shell').classList.remove('camera-shake');
    void $('#game-shell').offsetWidth;
    $('#game-shell').classList.add('camera-shake');
  }
}

function showCinematic(kicker, title) {
  const banner = $('#cinematic-banner');
  $('#cinematic-kicker').textContent = kicker;
  $('#cinematic-title').textContent = title;
  banner.classList.remove('hidden');
  banner.style.animation = 'none';
  void banner.offsetWidth;
  banner.style.animation = '';
  window.setTimeout(() => banner.classList.add('hidden'), 1700);
}

function showAchievementToast(title) {
  window.clearTimeout(achievementTimer);
  $('#achievement-title').textContent = title;
  const notice = $('#achievement-toast');
  notice.classList.remove('hidden');
  notice.style.animation = 'none';
  void notice.offsetWidth;
  notice.style.animation = '';
  achievementTimer = window.setTimeout(() => notice.classList.add('hidden'), 3500);
}

function rewardText(reward = {}) {
  return [reward.gold && `${compactNumber(reward.gold)} ทอง`, reward.wood && `${compactNumber(reward.wood)} ไม้`, reward.dew && `${compactNumber(reward.dew)} น้ำค้าง`, reward.tickets && `ตั๋วอัญเชิญ ×${reward.tickets}`, reward.research && `แต้มวิจัย ×${reward.research}`, reward.equipment && `อุปกรณ์ ${reward.equipment}`].filter(Boolean).join(' · ');
}

function showRewardReveal(title, reward, kicker = 'MISSION COMPLETE') {
  window.clearTimeout(rewardTimer);
  overlayReturnFocus = document.activeElement;
  $('#reward-kicker').textContent = kicker;
  $('#reward-title').textContent = title;
  $('#reward-copy').textContent = rewardText(reward);
  $('#reward-reveal').classList.remove('hidden');
  syncTutorialPresentation();
  window.requestAnimationFrame(() => $('.reward-card')?.focus());
  sound.play('collect');
}

function playSceneTransition() {
  const wipe = document.createElement('div');
  wipe.className = 'scene-transition';
  $('#game-shell').appendChild(wipe);
  window.setTimeout(() => wipe.remove(), 850);
}

function triggerScreenFlash(danger = false) {
  const flash = $('#screen-flash');
  flash.classList.remove('active', 'danger');
  void flash.offsetWidth;
  flash.classList.toggle('danger', danger);
  flash.classList.add('active');
}

function resourceLabel(cost) {
  const parts = [];
  if (cost.gold) parts.push(`${svgIcon('coin')}${compactNumber(cost.gold)}`);
  if (cost.wood) parts.push(`${svgIcon('wood')}${compactNumber(cost.wood)}`);
  if (cost.dew) parts.push(`${svgIcon('crystal')}${compactNumber(cost.dew)}`);
  if (cost.tickets) parts.push(`${svgIcon('portal')}${compactNumber(cost.tickets)}`);
  return `<span class="resource-cost">${parts.map(part => `<span>${part}</span>`).join('')}</span>`;
}

function buildingVisual(type) {
  const icons = { mint: 'coin', timber: 'wood', grove: 'wood', well: 'crystal', dew: 'crystal', rally: 'shield', arrow: 'swords', vault: 'shield', builder: 'builder', scout: 'globe', workshop: 'build', bell: 'spark', guardian: 'shield', laboratory: 'portal' };
  return svgIcon(icons[type] || 'build');
}

function troopVisual(type) {
  return svgIcon({ runner: 'wind', smith: 'shield', lotus: 'spark', ember: 'swords' }[type] || 'shield');
}

function buildingUpgradePreview(building, def) {
  const current = building.level;
  const next = Math.min(5, current + 1);
  const hpNow = Math.round(def.hp * (1 + (current - 1) * .18));
  const hpNext = Math.round(def.hp * (1 + (next - 1) * .18));
  let specialty = 'พลังเมือง';
  if (['mint'].includes(building.type)) specialty = `ผลิตทอง +${next * 20}%`;
  else if (['timber', 'grove'].includes(building.type)) specialty = `ผลิตไม้ +${next * 20}%`;
  else if (['dew', 'well'].includes(building.type)) specialty = `น้ำค้างฟ้า +${next * 15}%`;
  else if (['arrow', 'guardian', 'bell'].includes(building.type)) specialty = `พลังป้องกัน +${next * 18}%`;
  else if (building.type === 'rally') specialty = `ความจุกองทัพ +${next * 8}`;
  else if (building.type === 'hall') specialty = `ปลดล็อกอาคารระดับ ${next}`;
  return `<div class="upgrade-preview"><span><small>ความทนทาน</small><b>${hpNow}</b><i>→</i><strong>${hpNext}</strong></span><span><small>โบนัสถัดไป</small><strong>${specialty}</strong></span></div>`;
}

function rarityPerk(rarity) {
  const rank = RARITIES[rarity]?.rank || 0;
  return rank === 3 ? 'Legendary Burst · คริติคอลและตราประทับสูงสุด' : rank === 2 ? 'Spirit Surge · โอกาสคริติคอลสูง' : rank === 1 ? 'Wind Tempo · เคลื่อนที่เร็วขึ้น' : 'Core Training · สมดุลทุกด้าน';
}

function renderResources() {
  const caps = storageCaps(state);
  const builders = builderStatus(state);
  $('#gold-value').textContent = compactNumber(state.gold);
  $('#wood-value').textContent = compactNumber(state.spiritWood);
  $('#dew-value').textContent = compactNumber(state.skyDew);
  $('#player-level').textContent = state.level;
  $('#player-name').textContent = state.playerName;
  $('#ticket-badge').textContent = state.summonTickets;
  $('#ticket-badge').classList.toggle('hidden', state.summonTickets <= 0);
  $('#builder-count').textContent = `${builders.available}/${builders.total}`;
  $('#builder-count').parentElement.setAttribute('aria-label', `ช่างพร้อม ${builders.available} จาก ${builders.total}`);
  $('[data-resource="gold"]').title = `คลัง ${compactNumber(state.gold)}/${compactNumber(caps.gold)}`;
  $('[data-resource="wood"]').title = `คลัง ${compactNumber(state.spiritWood)}/${compactNumber(caps.wood)}`;
  $('[data-resource="dew"]').title = `คลัง ${compactNumber(state.skyDew)}/${compactNumber(caps.dew)}`;
  $('[data-resource="gold"]').setAttribute('aria-label', `ทอง ${compactNumber(state.gold)} จาก ${compactNumber(caps.gold)} แตะเพื่อเก็บผลผลิต`);
  $('[data-resource="wood"]').setAttribute('aria-label', `ไม้จิตวิญญาณ ${compactNumber(state.spiritWood)} จาก ${compactNumber(caps.wood)} แตะเพื่อเก็บผลผลิต`);
  $('[data-resource="dew"]').setAttribute('aria-label', `น้ำค้างฟ้า ${compactNumber(state.skyDew)} จาก ${compactNumber(caps.dew)} แตะเพื่อเก็บผลผลิต`);
  const cosmetics = state.monetization?.cosmetics || [];
  $('#game-shell').classList.toggle('cosmetic-dawn', cosmetics.includes('island_dawn'));
  $('#game-shell').classList.toggle('cosmetic-twilight', cosmetics.includes('island_twilight'));
  $('#game-shell').classList.toggle('cosmetic-gilded', cosmetics.includes('ui_gilded'));
  $('#profile-button').classList.toggle('founder-frame', cosmetics.includes('frame_founder'));
  $('#profile-button').classList.toggle('cloudlion-frame', cosmetics.includes('frame_cloudlion'));
  const available = production(state);
  $('#collect-label').textContent = available.gold + available.wood > 20
    ? `+${compactNumber(available.gold)} 🪙  +${compactNumber(available.wood)} 🪵` : 'กำลังผลิต';
  renderTutorialAccess();
  renderObjectiveUI();
}

function tutorialPanelAllowed(panel) {
  if (state.tutorialComplete || panel === 'settings') return true;
  const step = state.tutorialStep;
  if (panel === 'build') return [1, 2, 3, 7, 8].includes(step);
  if (panel === 'territory') return step === 8;
  if (['army', 'roster', 'summon'].includes(panel)) return step >= 4 && step <= 6;
  return false;
}

function renderTutorialAccess() {
  const locked = (selector, value) => {
    const element = $(selector);
    if (!element) return;
    element.disabled = value;
    element.setAttribute('aria-disabled', String(value));
  };
  locked('[data-panel="build"]', !tutorialPanelAllowed('build'));
  locked('[data-panel="army"]', !tutorialPanelAllowed('army'));
  locked('[data-panel="missions"]', !state.tutorialComplete);
  locked('[data-panel="multiplayer"]', !state.tutorialComplete);
  locked('[data-panel="menu"]', !state.tutorialComplete);
  locked('#collect-button', !state.tutorialComplete && state.tutorialStep !== 2);
  locked('#battle-button', !state.tutorialComplete && state.tutorialStep !== 6);
  $('#collect-button').classList.toggle('tutorial-suppressed', !state.tutorialComplete && state.tutorialStep !== 2);
  $('#battle-button').classList.toggle('tutorial-suppressed', !state.tutorialComplete && state.tutorialStep !== 6);
}

function renderObjectiveUI() {
  const missions = dailyMissionStatus(state);
  const readyCount = missions.filter(item => item.ready && !item.claimed).length;
  $('#mission-badge').textContent = readyCount || '!';
  $('#mission-badge').classList.toggle('hidden', readyCount <= 0);
  const button = $('#next-action');
  if (!state.tutorialComplete || world.mode !== 'city' || activePanel) {
    button.disabled = true;
    button.dataset.panel = '';
    button.dataset.action = '';
    button.classList.add('hidden');
  } else {
    const next = recommendedAction(state);
    $('#next-action-label').textContent = next.label;
    $('#next-action-detail').textContent = next.detail || '';
    button.dataset.panel = next.panel || '';
    button.dataset.action = next.action || '';
    button.disabled = false;
    button.classList.remove('hidden');
  }
  const newlyReady = achievementStatus(state).find(item => item.ready && !item.claimed && !announcedAchievements.has(item.id));
  if (newlyReady) {
    announcedAchievements.add(newlyReady.id);
    showAchievementToast(newlyReady.title);
  }
}

function setActiveTool(panel = '') {
  document.querySelectorAll('#city-tools [data-panel]').forEach(button => {
    const directMatch = button.dataset.panel === panel;
    const groupedMatch = (panel === 'territory' && button.dataset.panel === 'build') ||
      (['roster', 'summon'].includes(panel) && button.dataset.panel === 'army') ||
      (['adventure', 'research', 'armory'].includes(panel) && button.dataset.panel === 'missions') ||
      (panel === 'friends' && button.dataset.panel === 'multiplayer') ||
      (['store', 'settings'].includes(panel) && button.dataset.panel === 'menu');
    const active = directMatch || groupedMatch;
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', String(active));
  });
}

async function loadCity(showLoader = false) {
  currentBuilding = null;
  $('#selection-card').classList.add('hidden');
  if (showLoader) $('#loading').classList.remove('done');
  $('#hud').classList.add('hidden');
  $('#battle-hud').classList.add('hidden');
  $('#result').classList.add('hidden');
  $('#loading-text').textContent = state.buildings.length === 1 ? 'กำลังตื่นขึ้นบนเกาะร้าง…' : 'กำลังวางเมืองจากโมเดล Tripo…';
  $('#loading-fill').style.width = '12%';
  await world.showCity(state, ratio => { $('#loading-fill').style.width = `${20 + ratio * 75}%`; });
  $('#loading-fill').style.width = '100%';
  window.setTimeout(() => {
    $('#loading').classList.add('done');
    showTutorial();
  }, 250);
  $('#hud').classList.remove('hidden');
  renderResources();
}

function selectBuilding(building) {
  currentBuilding = building;
  const card = $('#selection-card');
  if (!building) { card.classList.add('hidden'); return; }
  const def = BUILDINGS[building.type];
  const cost = upgradeCost(building);
  const maxed = building.level >= 5;
  const constructing = Boolean(building.underConstruction);
  const tutorialUpgradeLocked = !state.tutorialComplete && !(state.tutorialStep === 7 && building.type === 'hall');
  const remaining = constructing ? Math.max(0, Math.ceil((building.readyAt - Date.now()) / 1000)) : 0;
  card.innerHTML = `<div class="selection-icon">${buildingVisual(building.type)}</div><div><h3>${def.name} <small>Lv.${building.level}</small></h3><p>${constructing ? `ช่างกำลังทำงาน · เหลือประมาณ ${remaining} วินาที` : tutorialUpgradeLocked ? 'ลูมี่จะเปิดการอัปเกรดในขั้นตอนที่เหมาะสม' : `${def.role} · ${maxed ? 'พัฒนาถึงระดับสูงสุดแล้ว' : 'ดูผลลัพธ์ก่อนใช้ทรัพยากร'}`}</p><div class="level-track"><i style="width:${building.level * 20}%"></i></div>${maxed || constructing || tutorialUpgradeLocked ? '' : buildingUpgradePreview(building, def)}</div><div class="selection-actions"><button class="move-button" type="button" ${building.type === 'hall' || constructing ? 'disabled' : ''}>ย้าย</button><button class="rotate-button" type="button" ${building.type === 'hall' || constructing ? 'disabled' : ''}>หมุน</button><button class="upgrade-button" type="button" ${maxed || constructing || tutorialUpgradeLocked ? 'disabled' : ''}>${constructing ? 'กำลังสร้าง' : tutorialUpgradeLocked ? 'รอบทสอน' : maxed ? 'เต็มแล้ว' : 'อัปเกรด'}<small>${constructing ? `${remaining} วิ` : tutorialUpgradeLocked ? 'ทำตามลำดับ' : maxed ? 'เลเวลสูงสุด' : resourceLabel(cost)}</small></button></div>`;
  card.classList.remove('hidden');
  card.querySelector('.move-button').addEventListener('click', () => {
    if (!currentBuilding || !world.beginMove(currentBuilding.id)) return;
    card.classList.add('hidden');
    toast('แตะตำแหน่งว่างบนเกาะเพื่อวางอาคาร');
  });
  card.querySelector('.rotate-button').addEventListener('click', async () => {
    if (!currentBuilding) return;
    const result = moveBuilding(state, currentBuilding.id, currentBuilding.x, currentBuilding.z, (currentBuilding.rotation || 0) + 1);
    if (!result.ok) return toast(result.reason);
    persist();
    await loadCity();
    toast(`หมุน ${def.name} แล้ว`);
  });
  card.querySelector('.upgrade-button').addEventListener('click', async () => {
    if (!currentBuilding) return;
    const result = upgradeBuilding(state, currentBuilding.id, { instant: !state.tutorialComplete });
    if (!result.ok) return toast(result.reason);
    sound.play('upgrade');
    recordProgress(state, 'upgrade');
    toast(result.queued ? `เริ่มอัปเกรด ${def.name} เป็น Lv.${result.targetLevel}` : `${def.name} ขึ้นเป็นเลเวล ${result.building.level} แล้ว ✨`);
    if (!state.tutorialComplete && state.tutorialStep === 7 && result.building.type === 'hall') advanceTutorial(8);
    else persist();
    await loadCity();
  });
}

function collect() {
  const reward = collectProduction(state);
  recordProgress(state, 'collect');
  sound.play('collect');
  toast(`เก็บแล้ว +${compactNumber(reward.gold)} ทอง · +${compactNumber(reward.wood)} ไม้`);
  if (!state.tutorialComplete && state.tutorialStep === 2) advanceTutorial(3);
  else persist();
}

const panels = {
  build: { kicker: 'เมือง', title: 'สร้างและพัฒนา', tabs: ['build', 'territory'] },
  territory: { kicker: 'เมือง', title: 'ขยายอาณาเขต', tabs: ['build', 'territory'] },
  army: { kicker: 'กองทัพ', title: 'ฝึกนักรบ', tabs: ['army', 'roster', 'summon'] },
  roster: { kicker: 'กองทัพ', title: 'ตัวละครของฉัน', tabs: ['army', 'roster', 'summon'] },
  summon: { kicker: 'กองทัพ', title: 'Spirit Gate', tabs: ['army', 'roster', 'summon'] },
  missions: { kicker: 'ความก้าวหน้า', title: 'ภารกิจวันนี้', tabs: ['missions', 'adventure', 'research', 'armory'] },
  adventure: { kicker: 'ความก้าวหน้า', title: 'แคมเปญหมู่เกาะ', tabs: ['missions', 'adventure', 'research', 'armory'] },
  research: { kicker: 'ความก้าวหน้า', title: 'งานวิจัย', tabs: ['missions', 'adventure', 'research', 'armory'] },
  armory: { kicker: 'ความก้าวหน้า', title: 'คลังอุปกรณ์', tabs: ['missions', 'adventure', 'research', 'armory'] },
  multiplayer: { kicker: 'สังคม', title: 'เล่นออนไลน์', tabs: ['multiplayer', 'friends'] },
  friends: { kicker: 'สังคม', title: 'เพื่อนของฉัน', tabs: ['multiplayer', 'friends'] },
  menu: { kicker: 'ศูนย์ควบคุม', title: 'เมนูเพิ่มเติม', tabs: ['menu', 'store', 'settings'] },
  store: { kicker: 'ศูนย์ควบคุม', title: 'ร้านผู้พิทักษ์', tabs: ['menu', 'store', 'settings'] },
  settings: { kicker: 'ศูนย์ควบคุม', title: 'ตั้งค่าเกม', tabs: ['menu', 'store', 'settings'] }
};

const tabNames = { build: 'อาคาร', territory: 'อาณาเขต', army: 'ฝึกทัพ', roster: 'ฮีโร่', summon: 'อัญเชิญ', missions: 'ภารกิจ', adventure: 'แผนที่', research: 'วิจัย', armory: 'อุปกรณ์', multiplayer: 'ออนไลน์', friends: 'เพื่อน', menu: 'ภาพรวม', store: 'ร้านค้า', settings: 'ตั้งค่า' };

function openSheet(panel) {
  if (!tutorialPanelAllowed(panel)) {
    toast('ทำตามขั้นตอนของลูมี่ก่อน แล้วเมนูนี้จะปลดล็อก');
    return;
  }
  const wasHidden = $('#sheet').classList.contains('hidden');
  if (wasHidden) sheetReturnFocus = document.activeElement;
  activePanel = panel;
  setActiveTool(panel);
  $('#game-shell').classList.add('sheet-open');
  renderObjectiveUI();
  sound.play('ui-tap');
  if (panel === 'roster' || panel === 'summon') preloadGroup('characters', 2).catch(() => {});
  if (panel === 'build') preloadGroup('buildings', 2).catch(() => {});
  const meta = panels[panel] || panels.build;
  $('#sheet').dataset.panel = panel;
  $('#sheet-kicker').textContent = meta.kicker;
  $('#sheet-title').textContent = meta.title;
  $('#sheet-tabs').innerHTML = meta.tabs.length > 1 ? meta.tabs.map(tab => `<button id="sheet-tab-${tab}" role="tab" aria-selected="${tab === panel}" aria-controls="sheet-content" class="${tab === panel ? 'active' : ''}" data-tab="${tab}" type="button">${tabNames[tab]}</button>`).join('') : '';
  $('#sheet-tabs').setAttribute('role', meta.tabs.length > 1 ? 'tablist' : 'navigation');
  $('#sheet-content').setAttribute('aria-labelledby', meta.tabs.length > 1 ? `sheet-tab-${panel}` : 'sheet-title');
  $('#sheet').classList.remove('hidden');
  $('#sheet-backdrop').classList.remove('hidden');
  $('#sheet-tabs').querySelectorAll('button').forEach(button => button.addEventListener('click', () => openSheet(button.dataset.tab)));
  renderPanel(panel);
  syncTutorialPresentation();
  window.requestAnimationFrame(() => (wasHidden ? $('#sheet-close') : $(`#sheet-tab-${panel}`))?.focus());
}

function closeSheet() {
  activePanel = '';
  setActiveTool();
  $('#game-shell').classList.remove('sheet-open');
  $('#sheet').classList.add('hidden');
  $('#sheet-backdrop').classList.add('hidden');
  tutorialGuided = false;
  syncTutorialPresentation();
  renderObjectiveUI();
  const target = sheetReturnFocus;
  sheetReturnFocus = null;
  window.requestAnimationFrame(() => target?.isConnected && target.focus());
}
function shopCard({ icon, title, body, meta, label, action, disabled = false, orange = false }) {
  return `<article class="shop-card"><span class="shop-icon">${icon}</span><div><h3>${title}</h3><p>${body}</p><b>${meta}</b></div><button type="button" data-action="${action}" class="${orange ? 'orange' : ''}" ${disabled ? 'disabled' : ''}>${label}</button></article>`;
}

function renderPanel(panel) {
  if (panel === 'build') renderBuildPanel();
  else if (panel === 'territory') renderTerritoryPanel();
  else if (panel === 'army') renderArmyPanel();
  else if (panel === 'roster') renderRosterPanel();
  else if (panel === 'summon') renderSummonPanel();
  else if (panel === 'missions') renderMissionsPanel();
  else if (panel === 'adventure') renderAdventurePanel();
  else if (panel === 'research') renderResearchPanel();
  else if (panel === 'armory') renderArmoryPanel();
  else if (panel === 'multiplayer') renderMultiplayerPanel();
  else if (panel === 'friends') renderFriendsPanel();
  else if (panel === 'menu') renderMenuPanel();
  else if (panel === 'store') renderStorePanel();
  else renderSettingsPanel();
}

function renderMenuPanel() {
  const accountLabel = auth.user ? 'เชื่อมบัญชีแล้ว' : 'กำลังเล่นแบบ Guest';
  const onlineLabel = auth.user ? (online.online ? 'ออนไลน์พร้อมเล่นกับเพื่อน' : 'กำลังเชื่อมต่อเครือข่าย') : 'ล็อกอินเมื่อต้องการเล่นออนไลน์';
  $('#sheet-content').innerHTML = `
    <section class="command-overview">
      <div><small>PLAYER STATUS</small><h3>${escapeHtml(state.playerName)} · Lv.${state.level}</h3><p>${accountLabel} · ${onlineLabel}</p></div>
      <span>${svgIcon('menu')}</span>
    </section>
    <div class="command-grid">
      <button data-action="open-panel:adventure" type="button"><span>${svgIcon('flag')}</span><strong>Campaign</strong><small>ด่าน Boss, Daily Rift และ Replay</small></button>
      <button data-action="open-panel:summon" type="button"><span>${svgIcon('portal')}<b>${state.summonTickets || 0}</b></span><strong>Spirit Gate</strong><small>อัญเชิญฮีโร่และดูการันตี</small></button>
      <button data-action="open-panel:store" type="button"><span>${svgIcon('crystal')}</span><strong>ร้านผู้พิทักษ์</strong><small>ของตกแต่งแบบไม่ Pay-to-win</small></button>
      <button data-action="open-panel:settings" type="button"><span>${svgIcon('settings')}</span><strong>ตั้งค่าและบัญชี</strong><small>เสียง ชื่อผู้เล่น และการซิงก์</small></button>
      <button data-action="open-panel:friends" type="button"><span>${svgIcon('friends')}</span><strong>Friend Code</strong><small>เพิ่มเพื่อนและบุกฐาน</small></button>
    </div>`;
  bindPanelActions();
}

function renderBuildPanel() {
  const offers = ['mint', 'timber', 'grove', 'well', 'rally', 'arrow', 'vault', 'dew', 'builder', 'scout', 'workshop', 'bell', 'guardian', 'laboratory'];
  const tutorialRequired = state.tutorialStep === 1 ? 'mint' : state.tutorialStep === 3 ? 'rally' : '';
  const builders = builderStatus(state);
  const layoutCount = layoutSlotCount(state);
  const layoutButtons = Array.from({ length: layoutCount }, (_, index) => `<div class="layout-slot"><strong>แปลน ${index + 1}</strong><button data-action="save-layout:${index}">บันทึก</button><button data-action="load-layout:${index}" ${state.baseLayouts?.[index] ? '' : 'disabled'}>ใช้แปลน</button></div>`).join('');
  $('#sheet-content').innerHTML = `<div class="section-note">Spirit Hall Lv.${hallLevel(state)} · ช่างว่าง ${builders.available}/${builders.total} · งานก่อสร้างทำงานต่อแม้ปิดเกม</div><div class="layout-manager">${layoutButtons}</div>` + offers.map(type => {
    const def = BUILDINGS[type];
    const cost = buildingCost(type);
    const locked = hallLevel(state) < def.unlockHall;
    const tutorialLocked = !state.tutorialComplete && type !== tutorialRequired;
    return shopCard({ icon: buildingVisual(type), title: def.name, body: def.role, meta: locked ? `ปลดล็อก Hall Lv.${def.unlockHall}` : tutorialLocked ? 'ลูมี่จะปลดล็อกตามขั้นตอนบทสอน' : resourceLabel(cost), label: locked ? 'ล็อก' : tutorialLocked ? 'รอตามขั้นตอน' : builders.available <= 0 ? 'ช่างไม่ว่าง' : 'สร้าง', action: `build:${type}`, disabled: locked || tutorialLocked || builders.available <= 0 || !canAfford(state, cost) });
  }).join('');
  bindPanelActions();
  if (tutorialRequired) focusSoon(`[data-action="build:${tutorialRequired}"]`);
}

function renderStorePanel() {
  const owned = new Set(state.monetization?.entitlements || []);
  const cosmetics = new Set(state.monetization?.cosmetics || []);
  const realProducts = STORE_PRODUCTS.map(product => {
    const permanentOwned = product.type !== 'consumable' && owned.has(product.id);
    return `<article class="store-card"><div><small>${product.badge || product.category.toUpperCase()}</small><h3>${product.title}</h3><p>${product.description}</p></div><button data-action="buy-product:${product.id}" ${permanentOwned || !billing.available ? 'disabled' : ''}>${permanentOwned ? 'มีแล้ว' : `${product.priceTHB} บาท`}</button></article>`;
  }).join('');
  const cosmeticItems = COSMETIC_CATALOG.map(item => `<article class="store-card cosmetic"><span>${svgIcon('spark')}</span><div><h3>${item.title}</h3><p>${item.description}</p></div><button data-action="buy-cosmetic:${item.id}" ${cosmetics.has(item.id) ? 'disabled' : ''}>${cosmetics.has(item.id) ? 'มีแล้ว' : `${item.cost} ✦`}</button></article>`).join('');
  $('#sheet-content').innerHTML = `<section class="fair-store"><small>NO PAY-TO-WIN</small><h3>เติมเพื่อความสวยและความสะดวก</h3><p>ไม่มีพลังรบ ทรัพยากร หรือกล่องสุ่มที่ซื้อด้วยเงินจริง</p><strong>Cosmetic Crystal: ${compactNumber(state.monetization?.crystals || 0)} ✦</strong></section>${billing.available ? '' : '<div class="section-note">Preview บนเว็บ: ปิดปุ่มเงินจริงไว้ การขายจริงต้องผ่าน StoreKit / Google Play และตรวจใบเสร็จที่เซิร์ฟเวอร์</div>'}<h3 class="store-heading">แพ็กและสิทธิ์ถาวร</h3><div class="store-grid">${realProducts}</div><h3 class="store-heading">ของตกแต่ง</h3><div class="store-grid">${cosmeticItems}</div><button id="restore-purchases" class="restore-purchases" type="button" ${billing.available ? '' : 'disabled'}>คืนค่ารายการซื้อ</button>`;
  bindPanelActions();
  $('#restore-purchases').addEventListener('click', async () => {
    try { await billing.restore(); toast('ส่งคำขอคืนค่ารายการซื้อแล้ว'); }
    catch (error) { toast(error.message); }
  });
}

function renderTerritoryPanel() {
  const content = EXPANSIONS.map((expansion, index) => {
    const unlocked = index < state.territoryLevel;
    const active = index === state.territoryLevel;
    const hallLocked = hallLevel(state) < expansion.requiredHall;
    return shopCard({ icon: svgIcon(unlocked ? 'check' : index === 0 ? 'wind' : index === 1 ? 'build' : 'crystal'), title: `${index + 1}. ${expansion.name}`, body: `${expansion.reward} · ต้องการ Hall Lv.${expansion.requiredHall}`, meta: unlocked ? 'ปลดล็อกแล้ว' : resourceLabel(expansion.cost), label: unlocked ? 'สำเร็จ' : hallLocked ? 'Hall ต่ำ' : active ? 'ขยาย' : 'ล็อก', action: active ? 'expand' : 'none', disabled: !active || hallLocked || !canAfford(state, expansion.cost), orange: active && !hallLocked });
  }).join('');
  $('#sheet-content').innerHTML = `<div class="section-note">แต่ละเกาะเพิ่มพื้นที่จริง อาคารพิเศษ และเส้นทาง progression ใหม่</div>${content}`;
  bindPanelActions();
  if (!state.tutorialComplete && state.tutorialStep === 8) focusSoon('[data-action="expand"]');
}

function renderArmyPanel() {
  const capacity = 12 + state.buildings.filter(b => b.type === 'rally').reduce((sum, b) => sum + b.level * 8, 0);
  const used = Object.entries(state.army).reduce((sum, [type, count]) => sum + TROOPS[type].housing * count, 0);
  $('#sheet-content').innerHTML = `<div class="section-note">กำลังพล ${used}/${capacity} · Unit มีบทบาทตายตัว ส่วน Leader เสริมค่าสเตตัสสูงสุดแบบจำกัดและไม่เปลี่ยนหน้าที่ของสาย</div>` + Object.entries(TROOPS).map(([type, troop]) => {
    const hero = combatTroop(state, type);
    return shopCard({ icon: troopVisual(type), title: hero ? `${troop.name} ×${state.army[type] || 0}` : `${troop.name} · Hall ยังไม่ถึง`, body: hero ? `Leader: ${hero.leaderName} (${hero.leaderRarity}) Lv.${hero.level} · HP ${hero.hp} · ATK ${hero.damage}<br><span class="perk-line">✦ ${hero.perk}</span>` : `ปลดล็อกที่ Hall Lv.${troop.unlockHall}`, meta: hero ? `${resourceLabel({ wood: troop.cost })} · ใช้ที่ ${troop.housing}` : 'พัฒนาฐานก่อน', label: hero ? 'ฝึก' : 'ล็อก', action: `train:${type}`, disabled: !hero || state.spiritWood < troop.cost });
  }).join('');
  bindPanelActions();
  if (!state.tutorialComplete && state.tutorialStep === 5) focusSoon('[data-action="train:runner"]');
}

function renderRosterPanel() {
  const ownedCount = Object.keys(state.heroes).length;
  $('#sheet-content').innerHTML = `<div class="section-note">สะสมแล้ว ${ownedCount}/${Object.keys(CHARACTERS).length} · ตัวซ้ำเปลี่ยนเป็น shard สำหรับเพิ่มเลเวล</div><div class="rarity-key">${Object.entries(RARITIES).map(([key, rarity]) => `<span style="background:${rarity.color}">${key} · ×${rarity.multiplier}</span>`).join('')}</div><div class="roster-grid">${Object.entries(CHARACTERS).map(([id, hero]) => heroCard(id, hero)).join('')}</div>`;
  bindPanelActions();
}

function heroCard(id, hero) {
  const owned = state.heroes[id];
  const active = state.activeHeroes[hero.type] === id;
  const rarity = RARITIES[hero.rarity];
  return `<article class="hero-card ${owned ? '' : 'locked'}" style="--rarity:${rarity.color}"><span class="hero-icon">${owned ? hero.icon : '❔'}</span><em>${hero.rarity}</em><h4>${owned ? hero.name : '???'}</h4><p>${owned ? hero.title : 'เรียกจาก Spirit Gate'} · ${TROOPS[hero.type].name}</p>${owned ? `<div class="hero-perk">✦ ${rarityPerk(hero.rarity)}</div>` : ''}<footer>${owned ? `<span>Lv.${owned.level} · shard ${owned.shards}</span><button data-action="${active ? `hero-upgrade:${id}` : `activate:${id}`}">${active ? 'เพิ่มเลเวล' : 'เลือกใช้'}</button>` : `<span>${rarity.name}</span>`}${active ? '<i class="active-chip">กำลังใช้</i>' : ''}</footer></article>`;
}

function renderSummonPanel() {
  const odds = SUMMON_ODDS.map(item => `<span style="color:${RARITIES[item.rarity].color}">${item.rarity} ${item.chance}%</span>`).join('');
  $('#sheet-content').innerHTML = `<div class="summon-banner"><small>LIMITLESS SKY</small><h3>Spirit Gate</h3><p>เรียก Leader มานำแต่ละสาย โบนัส rarity ถูกจำกัดและทุกสายฝึกได้จาก Hall โดยไม่ต้องสุ่ม</p><div class="odds-row">${odds}</div></div><div class="pity-box">การันตี SR ทุก 10 ครั้ง · UR ไม่เกิน 50 ครั้ง<br>สะสมปัจจุบัน: SR ${state.srPity}/10 · UR ${state.summonPity}/50</div><div class="summon-actions"><button data-action="summon:1">อัญเชิญ 1 ครั้ง<small>${state.summonTickets ? `🎟️ ตั๋ว ×${state.summonTickets}` : '💎 120'}</small></button><button class="ten" data-action="summon:10">อัญเชิญ 10 ครั้ง<small>💎 1,080 · การันตี SR</small></button></div>`;
  bindPanelActions();
  if (!state.tutorialComplete && state.tutorialStep === 4) focusSoon('[data-action="summon:1"]');
}

function renderMissionsPanel() {
  const missions = dailyMissionStatus(state);
  const achievements = achievementStatus(state);
  const completed = missions.filter(item => item.claimed).length;
  const chestReady = completed === missions.length && !state.progression.chestClaimed;
  const icons = { collect: 'spark', train: 'shield', battle: 'swords' };
  $('#sheet-content').innerHTML = `
    <section class="mission-summary"><small>DAILY FLIGHT PLAN</small><h3>${completed}/${missions.length} ภารกิจสำเร็จ</h3><p>ทำเป้าหมายสั้น ๆ เพื่อให้เมืองเติบโตทุกครั้งที่กลับมาเล่น</p></section>
    ${missions.map(mission => `<article class="mission-card ${mission.ready ? 'ready' : ''} ${mission.claimed ? 'claimed' : ''}">
      <span class="mission-icon"><svg class="ui-icon"><use href="#i-${icons[mission.id] || 'quest'}"/></svg></span>
      <div><h4>${mission.title}</h4><p>${mission.detail} · ${resourceLabel(mission.reward)}</p><div class="mission-track"><i style="width:${mission.progress / mission.target * 100}%"></i></div></div>
      <button data-action="claim-mission:${mission.id}" type="button" ${mission.ready && !mission.claimed ? '' : 'disabled'}>${mission.claimed ? 'รับแล้ว' : mission.ready ? 'รับรางวัล' : `${mission.progress}/${mission.target}`}</button>
    </article>`).join('')}
    <article class="daily-chest"><span>${svgIcon('gift')}</span><div><h4>หีบผู้พิทักษ์ประจำวัน</h4><p>รับครบทั้ง ${missions.length} ภารกิจ · ${resourceLabel({ gold: 750, wood: 520, dew: 80, tickets: 1 })}</p></div><button data-action="claim-chest" type="button" ${chestReady ? '' : 'disabled'}>${state.progression.chestClaimed ? 'เปิดแล้ว' : chestReady ? 'เปิดหีบ' : `${completed}/${missions.length}`}</button></article>
    <h3 class="achievement-heading">ความสำเร็จระยะยาว</h3>
    ${achievements.map(item => `<article class="achievement-card"><div><h4>${item.title}</h4><p>${item.detail}</p><em>${item.progress}/${item.target} · ${resourceLabel(item.reward)}</em></div><button data-action="claim-achievement:${item.id}" type="button" ${item.ready && !item.claimed ? '' : 'disabled'}>${item.claimed ? 'รับแล้ว' : item.ready ? 'รับ' : `${item.progress}/${item.target}`}</button></article>`).join('')}`;
  bindPanelActions();
}

function armyPower() {
  return Object.entries(state.army).reduce((sum, [type, count]) => {
    const troop = combatTroop(state, type);
    return sum + (troop ? Math.round((troop.hp * .18 + troop.damage * 4.5) * count) : 0);
  }, 0);
}

function bonusLabel(bonus = {}) {
  const labels = { hp: 'HP', damage: 'ATK', speed: 'SPD', criticalChance: 'CRIT', healing: 'HEAL' };
  return Object.entries(bonus).map(([key, value]) => `${labels[key] || key} +${Math.round(value * 100)}%`).join(' · ');
}

function renderAdventurePanel() {
  const adventure = ensureAdventure(state);
  const stages = stageStatus(state);
  const dungeon = todayDungeon(state);
  const power = armyPower();
  $('#sheet-content').innerHTML = `
    <section class="adventure-hero"><div><small>SKY ARCHIPELAGO</small><h3>แคมเปญหมู่เกาะ</h3><p>ผ่านด่านเพื่อปลดล็อก Biome, อุปกรณ์ และแต้มวิจัย</p></div><span><small>พลังสำรวจ</small><strong>${svgIcon('spark')} ${adventure.energy}/5</strong><em>พลังทัพ ${compactNumber(power)}</em></span></section>
    <article class="dungeon-card ${dungeon.claimed ? 'claimed' : ''}"><span>${svgIcon('portal')}</span><div><small>DAILY RIFT</small><h4>${dungeon.title}</h4><p>รางวัลรายวัน · ${resourceLabel(dungeon.reward)} · วิจัย ${dungeon.reward.research}</p></div><button data-action="dungeon:today" ${dungeon.claimed ? 'disabled' : ''}>${dungeon.claimed ? 'สำเร็จแล้ว' : 'เข้ารอยแยก'}</button></article>
    <div class="campaign-path">${stages.map(stage => `<article class="campaign-node ${stage.unlocked ? '' : 'locked'} ${stage.boss ? 'boss' : ''}">
      <div class="campaign-index">${stage.boss ? svgIcon('shield') : stage.index + 1}</div><div><small>${stage.biome}</small><h4>${stage.title}</h4><p>พลังแนะนำ ${compactNumber(stage.power)} · ใช้พลัง ${stage.energy}</p><em>${[0,1,2].map(i => i < stage.stars ? '★' : '☆').join(' ')}</em></div>
      <button data-action="campaign:${stage.id}" ${!stage.unlocked || adventure.energy < stage.energy ? 'disabled' : ''}>${stage.unlocked ? stage.stars ? 'เล่นอีกครั้ง' : 'เริ่มด่าน' : 'ล็อก'}</button>
    </article>`).join('')}</div>
    <h3 class="adventure-heading">Replay ล่าสุด</h3>
    <div class="replay-list">${adventure.replays.length ? adventure.replays.map(replay => `<article><span>${svgIcon(replay.marks ? 'swords' : 'globe')}</span><div><strong>${escapeHtml(replay.enemy?.name || 'ฐานคู่ต่อสู้')}</strong><small>${replay.percent}% · ${new Date(replay.createdAt).toLocaleDateString('th-TH')}</small></div><button data-action="replay:${replay.id}">ดู Replay</button></article>`).join('') : '<p class="empty-replay">จบรบหนึ่งครั้งเพื่อบันทึก Replay อัตโนมัติ</p>'}</div>`;
  bindPanelActions();
}

function renderResearchPanel() {
  const adventure = ensureAdventure(state);
  $('#sheet-content').innerHTML = `<section class="research-header"><span>${svgIcon('portal')}</span><div><small>SPIRIT KNOWLEDGE</small><h3>แต้มวิจัย ${adventure.researchPoints}</h3><p>ได้รับจากการผ่าน Campaign และ Daily Rift</p></div></section><div class="research-grid">${RESEARCH_NODES.map(node => {
    const level = adventure.research[node.id] || 0;
    const maxed = level >= node.max;
    const cost = maxed ? 0 : node.costs[level];
    return `<article><span>${svgIcon({ hp: 'shield', damage: 'swords', speed: 'wind', criticalChance: 'spark', production: 'coin', storage: 'crystal' }[node.stat] || 'spark')}</span><div><h4>${node.title}</h4><p>${node.detail}</p><div class="research-pips">${Array.from({length:node.max},(_,i)=>`<i class="${i < level ? 'filled' : ''}"></i>`).join('')}</div></div><button data-action="research:${node.id}" ${maxed || adventure.researchPoints < cost ? 'disabled' : ''}>${maxed ? 'MAX' : `วิจัย · ${cost}`}</button></article>`;
  }).join('')}</div>`;
  bindPanelActions();
}

function renderArmoryPanel() {
  const adventure = ensureAdventure(state);
  $('#sheet-content').innerHTML = `<section class="armory-header"><span>${svgIcon('swords')}</span><div><small>EQUIPMENT LOADOUT</small><h3>อุปกรณ์หัวหน้ากอง</h3><p>อุปกรณ์ได้จากการเล่นเท่านั้น ไม่มีขายพลังในร้านเงินจริง</p></div></section><div class="armory-grid">${EQUIPMENT_ITEMS.map(item => {
    const owned = adventure.inventory.includes(item.id);
    const equipped = adventure.equipped[item.type] === item.id;
    return `<article class="${owned ? '' : 'locked'}" style="--item-rarity:${RARITIES[item.rarity].color}"><span>${owned ? troopVisual(item.type) : svgIcon('shield')}</span><em>${item.rarity}</em><h4>${owned ? item.title : 'อุปกรณ์ที่ยังไม่ค้นพบ'}</h4><p>${bonusLabel(item.bonus)}</p><small>${TROOPS[item.type].name}</small><button data-action="equip:${item.id}" ${!owned || equipped ? 'disabled' : ''}>${equipped ? 'ติดตั้งอยู่' : owned ? 'ติดตั้ง' : 'ผ่าน Campaign'}</button></article>`;
  }).join('')}</div>`;
  bindPanelActions();
}

function multiplayerError(error) {
  const message = String(error?.message || error || 'Multiplayer ขัดข้อง');
  if (message === 'LOGIN_REQUIRED') return 'กรุณาล็อกอินก่อนเข้า Multiplayer';
  if (message === 'ROOM_FULL') return 'ห้องนี้มีผู้เล่นครบ 2 คนแล้ว ลองใช้ Quick Match หรือสร้างห้องใหม่';
  if (/หมดเวลา|timed out/i.test(message)) return 'เชื่อมต่อนานเกินไป กรุณาตรวจอินเทอร์เน็ตแล้วลองอีกครั้ง';
  if (/row-level security|permission denied/i.test(message)) return 'บัญชีนี้ยังไม่มีสิทธิ์เข้าห้อง กรุณาล็อกอินใหม่';
  return message;
}

function multiplayerIcon(name) {
  return `<svg class="ui-icon" aria-hidden="true"><use href="#i-${name}"/></svg>`;
}

function playerInitial(name) {
  return escapeHtml(String(name || 'S').trim().charAt(0).toUpperCase() || 'S');
}

function roomInviteUrl(code) {
  const url = new URL('/', window.location.origin);
  url.searchParams.set('room', code);
  return url.toString();
}

function messageTime(timestamp) {
  return new Date(Number(timestamp) || Date.now()).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });
}

async function shareRoomInvite(code) {
  const url = roomInviteUrl(code);
  if (navigator.share) {
    await navigator.share({ title: 'CLASH of Stamp', text: `เข้าห้องของฉันด้วย Room Code ${code}`, url });
    return 'แชร์คำเชิญแล้ว';
  }
  await navigator.clipboard?.writeText(url);
  return 'คัดลอกลิงก์เชิญแล้ว';
}

async function joinRoomFromInviteUrl() {
  const code = new URLSearchParams(window.location.search).get('room')?.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6) || '';
  if (!auth.user || code.length !== 6 || inviteJoinInFlight || multiplayer.room?.room_code === code) return;
  inviteJoinInFlight = true;
  try {
    await multiplayer.connectLobby();
    openSheet('multiplayer');
    await multiplayer.joinRoom(code);
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('room');
    window.history.replaceState({}, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
    renderMultiplayerPanel();
    toast(`เข้าห้อง ${code} แล้ว`);
  } catch (error) {
    openSheet('multiplayer');
    toast(multiplayerError(error), 3800);
  } finally {
    inviteJoinInFlight = false;
  }
}

async function renderMultiplayerPanel() {
  if (activePanel !== 'multiplayer') return;
  const inviteFromUrl = new URLSearchParams(window.location.search).get('room')?.toUpperCase().slice(0, 6) || '';
  if (!auth.user) {
    $('#sheet-content').innerHTML = `<section class="mp-app mp-auth-state">
      <div class="mp-auth-visual"><span>${multiplayerIcon('globe')}</span><i></i></div>
      <small class="mp-eyebrow">SECURE SKYLINK</small>
      <h3>${inviteFromUrl ? `เพื่อนชวนคุณเข้าห้อง ${escapeHtml(inviteFromUrl)}` : 'เล่นกับเพื่อนได้จากทุกอุปกรณ์'}</h3>
      <p>ล็อกอินครั้งเดียวเพื่อเปิด Lobby แบบเรียลไทม์ บุกฐานเพื่อน และเก็บประวัติการแข่งขันอย่างปลอดภัย</p>
      <div class="mp-benefits"><span>${multiplayerIcon('friends')}<b>ห้องสด</b><small>เห็นเพื่อนออนไลน์ทันที</small></span><span>${multiplayerIcon('swords')}<b>บุกฐานจริง</b><small>ผลการรบส่งถึงกัน</small></span><span>${multiplayerIcon('shield')}<b>เซฟส่วนตัว</b><small>ป้องกันด้วย RLS</small></span></div>
      <button id="multiplayer-login" class="mp-primary" type="button">${inviteFromUrl ? 'ล็อกอินและเข้าห้อง' : 'เข้าสู่ระบบ Multiplayer'}</button>
      <small class="mp-footnote">ยังเล่นเมืองแบบ Guest ได้ตามปกติ · ระบบออนไลน์ไม่กระทบเซฟในเครื่อง</small>
    </section>`;
    $('#multiplayer-login').addEventListener('click', showAuthGate);
    return;
  }

  if (!multiplayer.connected && !multiplayer.lobbyChannel) {
    $('#sheet-content').innerHTML = `<section class="mp-app mp-loading" aria-live="polite"><div class="mp-radar"><i></i><b></b><span>${multiplayerIcon('globe')}</span></div><strong>กำลังเชื่อม SkyLink…</strong><small>ตรวจสอบบัญชีและค้นหาผู้เล่นออนไลน์</small><div class="mp-skeleton"><i></i><i></i><i></i></div></section>`;
    try { await multiplayer.connectLobby(); }
    catch (error) {
      if (activePanel === 'multiplayer') $('#sheet-content').innerHTML = `<section class="mp-app mp-error" role="alert"><span>${multiplayerIcon('globe')}</span><small class="mp-eyebrow">CONNECTION PAUSED</small><h3>ยังเข้า Lobby ไม่ได้</h3><p>${escapeHtml(multiplayerError(error))}</p><div><button id="multiplayer-retry" class="mp-primary" type="button">ลองเชื่อมอีกครั้ง</button><button id="multiplayer-settings" class="mp-secondary" type="button">ตรวจบัญชี</button></div></section>`;
      $('#multiplayer-retry')?.addEventListener('click', () => { multiplayer.disconnect().finally(renderMultiplayerPanel); });
      $('#multiplayer-settings')?.addEventListener('click', () => openSheet('settings'));
      return;
    }
  }
  if (activePanel !== 'multiplayer') return;

  if (multiplayer.room) {
    const opponent = multiplayer.opponent;
    const self = multiplayer.roomUsers.find(player => player.userId === auth.user.id) || multiplayer.profile(true);
    const marks = message => Math.max(0, Math.min(3, Number(message.marks) || 0));
    const messages = multiplayer.messages.map(message => message.kind === 'system'
      ? `<div class="mp-message mp-system"><span>${multiplayerIcon('spark')}</span><p>${escapeHtml(message.text)}</p><time>${messageTime(message.sentAt)}</time></div>`
      : message.kind === 'result'
        ? `<div class="mp-message mp-result-message"><span>${multiplayerIcon('swords')}</span><p><b>${escapeHtml(message.name)}</b> บุกได้ ${Math.max(0, Math.min(100, Number(message.percent) || 0))}% <em>${'★'.repeat(marks(message))}${'☆'.repeat(3 - marks(message))}</em></p><time>${messageTime(message.sentAt)}</time></div>`
        : `<div class="mp-message ${message.userId === auth.user.id ? 'mine' : ''}"><span class="mp-mini-avatar">${playerInitial(message.name)}</span><p><b>${escapeHtml(message.name)}</b>${escapeHtml(message.text)}</p><time>${messageTime(message.sentAt)}</time></div>`).join('');
    const playerCard = (player, mine = false) => player
      ? `<article class="mp-player-card ${mine ? 'mine' : 'rival'}"><div class="mp-avatar">${playerInitial(player.name)}</div><div><small>${mine ? 'YOUR ISLAND' : 'RIVAL ISLAND'}</small><strong>${escapeHtml(player.name)}</strong><span>Lv.${Number(player.level) || 1} · ฐานพร้อมรบ</span></div><i aria-label="ออนไลน์"></i>${mine ? '<em>คุณ</em>' : ''}</article>`
      : `<article class="mp-player-card vacant"><div class="mp-avatar">?</div><div><small>RIVAL ISLAND</small><strong>กำลังรอคู่ต่อสู้</strong><span>แชร์ลิงก์หรือ Room Code ให้เพื่อน</span></div><i></i></article>`;
    $('#sheet-content').innerHTML = `<section class="mp-app mp-room" aria-live="polite">
      <header class="mp-statusbar"><div><i></i><strong>ห้องส่วนตัว</strong><small>${opponent ? '2/2 พร้อมรบ' : '1/2 รอผู้เล่น'}</small></div><span>${multiplayerIcon('shield')} เชื่อมต่อปลอดภัย</span></header>
      <section class="mp-room-ticket"><div><small>ROOM CODE</small><strong>${multiplayer.room.room_code}</strong><span>เปิดใช้งานอีกประมาณ 2 ชั่วโมง</span></div><div><button id="copy-room" type="button">คัดลอกโค้ด</button><button id="share-room" class="mp-share" type="button">${multiplayerIcon('friends')} แชร์คำเชิญ</button></div></section>
      <section class="mp-versus">${playerCard(self, true)}<div class="mp-vs"><i></i><strong>VS</strong><i></i></div>${playerCard(opponent)}</section>
      ${opponent ? `<section class="mp-ready-panel"><div>${multiplayerIcon('swords')}<span><small>คู่ต่อสู้พร้อมแล้ว</small><strong>บุกฐานของ ${escapeHtml(opponent.name)}</strong></span></div><button id="raid-live-player" class="mp-primary" type="button">เริ่มบุก</button></section>` : `<section class="mp-waiting"><div class="mp-radar"><i></i><b></b><span>${multiplayerIcon('friends')}</span></div><div><strong>กำลังรอเพื่อนเข้าห้อง</strong><p>เพื่อนกดลิงก์เชิญหรือนำโค้ด <b>${multiplayer.room.room_code}</b> ไปกรอกที่หน้า Multiplayer</p></div><ol><li class="done"><b>1</b>สร้างห้อง</li><li><b>2</b>เชิญเพื่อน</li><li><b>3</b>เริ่มบุก</li></ol></section>`}
      <section class="mp-chat"><header><div><strong>Room Comms</strong><small>ข้อความสดและผลการรบ</small></div><i class="mp-live-dot"></i></header><div class="mp-chat-log">${messages || `<div class="mp-chat-empty">${multiplayerIcon('friends')}<span><strong>ห้องสื่อสารพร้อมแล้ว</strong><small>ทักเพื่อนหรือส่งข้อความด่วนด้านล่าง</small></span></div>`}</div><div class="mp-quick-chat"><button data-chat="พร้อม!">พร้อม!</button><button data-chat="สู้กัน! ⚔️">สู้กัน!</button><button data-chat="ฐานสวยมาก ✨">ฐานสวยมาก</button><button data-chat="GG!">GG!</button></div><form id="room-chat-form"><label class="sr-only" for="room-chat-input">ข้อความในห้อง</label><input id="room-chat-input" maxlength="80" placeholder="พิมพ์ข้อความ…" autocomplete="off"><button type="submit" aria-label="ส่งข้อความ">ส่ง</button></form></section>
      <footer class="mp-room-footer"><span>ออกจากห้องได้ทุกเมื่อ เมืองและกองทัพจะไม่หาย</span><button id="leave-room" type="button">ออกจากห้อง</button></footer>
    </section>`;
    $('#copy-room').addEventListener('click', async () => { await navigator.clipboard?.writeText(multiplayer.room.room_code); toast('คัดลอก Room Code แล้ว'); });
    $('#share-room').addEventListener('click', () => shareRoomInvite(multiplayer.room.room_code).then(toast).catch(error => { if (error?.name !== 'AbortError') toast('แชร์ไม่สำเร็จ ลองคัดลอกโค้ดแทน'); }));
    $('#raid-live-player')?.addEventListener('click', () => startBattle({ name: opponent.name, base: opponent.base, multiplayerUserId: opponent.userId, roomCode: multiplayer.room.room_code }));
    $('#sheet-content').querySelectorAll('[data-chat]').forEach(button => button.addEventListener('click', () => multiplayer.sendChat(button.dataset.chat).catch(error => toast(multiplayerError(error)))));
    $('#room-chat-form').addEventListener('submit', event => {
      event.preventDefault(); const input = $('#room-chat-input'); multiplayer.sendChat(input.value).then(() => { input.value = ''; }).catch(error => toast(multiplayerError(error)));
    });
    $('#leave-room').addEventListener('click', async event => {
      event.currentTarget.disabled = true;
      try { await multiplayer.leaveRoom(); toast('ออกจากห้องแล้ว'); }
      catch (error) { toast(multiplayerError(error)); }
    });
    return;
  }

  const others = multiplayer.lobbyUsers.filter(player => player.userId !== auth.user.id);
  const invitations = multiplayer.invites.map(invite => `<article class="mp-invite"><div class="mp-avatar">${playerInitial(invite.fromName)}</div><div><small>คำเชิญใหม่</small><strong>${escapeHtml(invite.fromName)} ชวนคุณประลอง</strong><span>ห้อง ${escapeHtml(invite.roomCode)} · เมื่อสักครู่</span></div><button data-accept-invite="${escapeHtml(invite.roomCode)}" class="mp-primary" type="button">เข้าห้อง</button><button data-dismiss-invite="${escapeHtml(invite.roomCode)}" type="button" aria-label="ปฏิเสธคำเชิญ">×</button></article>`).join('');
  $('#sheet-content').innerHTML = `<section class="mp-app mp-lobby">
    <header class="mp-statusbar"><div><i></i><strong>SkyLink Online</strong><small>เชื่อมต่อแล้ว</small></div><span>${multiplayerIcon('friends')} ${multiplayer.lobbyUsers.length} ออนไลน์</span></header>
    ${invitations ? `<section class="mp-invites" aria-live="polite">${invitations}</section>` : ''}
    <section class="mp-lobby-hero"><div><small class="mp-eyebrow">REALTIME BATTLE LOBBY</small><h3>เลือกคู่ต่อสู้ แล้วออกศึก</h3><p>ค้นหาผู้เล่นที่พร้อมทันที หรือเปิดห้องส่วนตัวสำหรับเพื่อน</p><div class="mp-lobby-stats"><span><b>${Math.max(0, others.length)}</b><small>คู่ต่อสู้ออนไลน์</small></span><span><b>${state.battleWins || 0}</b><small>ชัยชนะของคุณ</small></span><span><b>1v1</b><small>รูปแบบห้อง</small></span></div></div><div class="mp-hero-orb">${multiplayerIcon('globe')}<i></i></div></section>
    <section class="mp-action-grid"><button id="quick-match" class="mp-match-card" type="button"><span>${multiplayerIcon('swords')}</span><div><small>แนะนำ</small><strong>Quick Match</strong><p>${others.length ? `มี ${others.length} คนพร้อมออนไลน์` : 'ถ้ายังไม่มีคู่ ระบบจะเปิดห้องรอให้'}</p></div><em>ค้นหาคู่ต่อสู้ →</em></button><button id="create-room" class="mp-private-card" type="button"><span>${multiplayerIcon('shield')}</span><div><strong>สร้างห้องส่วนตัว</strong><p>รับโค้ดและลิงก์สำหรับส่งให้เพื่อน</p></div><em>สร้างห้อง</em></button></section>
    <section class="mp-join-card"><div><span>${multiplayerIcon('friends')}</span><div><strong>มี Room Code แล้ว?</strong><small>กรอกโค้ด 6 ตัวเพื่อเข้าห้องเพื่อน</small></div></div><form id="join-room-form"><label class="sr-only" for="room-code-input">Room Code 6 ตัว</label><input id="room-code-input" maxlength="6" inputmode="text" placeholder="ABC123" autocomplete="off" value="${escapeHtml(inviteFromUrl)}"><button type="submit">เข้าห้อง</button></form></section>
    <section class="mp-panel"><header><div><strong>ผู้พิทักษ์ออนไลน์</strong><small>กดชวนเพื่อเปิดห้องให้ผู้เล่นคนนั้น</small></div><span class="mp-live-label"><i></i> LIVE</span></header><div class="mp-player-list">${others.length ? others.map(player => `<article class="mp-online-player"><div class="mp-avatar">${playerInitial(player.name)}</div><div><strong>${escapeHtml(player.name)}</strong><small>Lv.${Number(player.level) || 1}${player.friendCode ? ` · ${escapeHtml(player.friendCode)}` : ''}</small></div><span><i></i> พร้อม</span><button data-invite-player="${escapeHtml(player.userId)}" type="button">ชวน</button></article>`).join('') : `<div class="mp-empty">${multiplayerIcon('friends')}<div><strong>Lobby เงียบอยู่ตอนนี้</strong><small>สร้างห้องแล้วแชร์ลิงก์ให้เพื่อน หรือเปิดหน้านี้บนอีกอุปกรณ์</small></div></div>`}</div></section>
    <section id="match-history" class="mp-panel mp-history"><header><div><strong>ประวัติศึกออนไลน์</strong><small>ผลล่าสุดจาก Supabase</small></div><span>กำลังโหลด…</span></header><div class="mp-history-skeleton"><i></i><i></i></div></section>
  </section>`;

  const runRoomAction = async (button, action) => {
    if (!button) return;
    const original = button.innerHTML; button.disabled = true; button.textContent = 'กำลังเชื่อม…';
    try { await action(); if (activePanel === 'multiplayer') renderMultiplayerPanel(); }
    catch (error) { button.disabled = false; button.innerHTML = original; toast(multiplayerError(error), 3600); }
  };
  $('#quick-match').addEventListener('click', event => runRoomAction(event.currentTarget, () => multiplayer.quickMatch()));
  $('#create-room').addEventListener('click', event => runRoomAction(event.currentTarget, () => multiplayer.createRoom()));
  $('#join-room-form').addEventListener('submit', event => { event.preventDefault(); runRoomAction(event.submitter || event.currentTarget.querySelector('button'), () => multiplayer.joinRoom($('#room-code-input').value)); });
  $('#room-code-input').addEventListener('input', event => { event.target.value = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6); });
  $('#sheet-content').querySelectorAll('[data-invite-player]').forEach(button => button.addEventListener('click', () => {
    const player = others.find(item => item.userId === button.dataset.invitePlayer);
    runRoomAction(button, async () => { await multiplayer.invitePlayer(player); toast(`ส่งคำเชิญให้ ${player.name} แล้ว`); });
  }));
  $('#sheet-content').querySelectorAll('[data-accept-invite]').forEach(button => button.addEventListener('click', () => runRoomAction(button, async () => { const code = button.dataset.acceptInvite; multiplayer.dismissInvite(code); await multiplayer.joinRoom(code); })));
  $('#sheet-content').querySelectorAll('[data-dismiss-invite]').forEach(button => button.addEventListener('click', () => multiplayer.dismissInvite(button.dataset.dismissInvite)));

  try {
    const history = await multiplayer.history();
    if (activePanel !== 'multiplayer' || multiplayer.room) return;
    $('#match-history').innerHTML = `<header><div><strong>ประวัติศึกออนไลน์</strong><small>ผลล่าสุดจาก Supabase</small></div><span>${history.length} ศึก</span></header><div>${history.length ? history.map(match => `<article class="mp-history-row ${match.won ? 'win' : ''}"><span>${multiplayerIcon(match.won ? 'swords' : 'flag')}</span><div><small>${match.won ? 'VICTORY' : 'EXPEDITION'}</small><strong>${escapeHtml(match.opponent_name)}</strong><time>${new Date(match.created_at).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' })}</time></div><b>${match.destruction_percent}%<small>${'★'.repeat(match.spirit_marks)}${'☆'.repeat(3 - match.spirit_marks)}</small></b></article>`).join('') : `<div class="mp-empty">${multiplayerIcon('swords')}<div><strong>ยังไม่มีประวัติการรบ</strong><small>เริ่ม Quick Match แล้วผลศึกแรกจะบันทึกตรงนี้</small></div></div>`}</div>`;
  } catch { $('#match-history').innerHTML = `<header><div><strong>ประวัติศึกออนไลน์</strong></div><button id="history-retry" type="button">ลองใหม่</button></header><div class="mp-empty compact"><div><strong>โหลดประวัติไม่สำเร็จ</strong><small>ระบบ Lobby ยังใช้งานได้ตามปกติ</small></div></div>`; $('#history-retry')?.addEventListener('click', renderMultiplayerPanel); }
}

async function renderFriendsPanel() {
  const code = state.online.friendCode || '------';
  $('#sheet-content').innerHTML = `<section class="mp-app mp-friends"><header class="mp-statusbar"><div><i></i><strong>Terra Anvil Friends</strong><small>${online.online ? 'เชื่อมต่อแล้ว' : 'กำลังเชื่อมต่อ'}</small></div><span>${multiplayerIcon('shield')} Friend Code ถาวร</span></header><section class="mp-friend-pass"><div><small>FRIEND CODE ของคุณ</small><strong>${escapeHtml(code)}</strong><p>ส่งโค้ดนี้ให้เพื่อนเพื่อเพิ่มกันและบุกฐานได้ทุกเวลา</p></div><button id="copy-friend-code" type="button">คัดลอกโค้ด</button></section><section class="mp-add-friend"><div><span>${multiplayerIcon('friends')}</span><div><strong>เพิ่มผู้พิทักษ์</strong><small>กรอก Friend Code ของเพื่อน</small></div></div><form id="friend-form"><label class="sr-only" for="friend-input">Friend Code</label><input id="friend-input" maxlength="8" placeholder="FRIEND CODE" autocomplete="off"><button type="submit">เพิ่มเพื่อน</button></form></section><section id="friend-list" class="mp-panel"><header><div><strong>รายชื่อเพื่อน</strong><small>แตะบุกเพื่อเยี่ยมฐาน</small></div><span>${online.online ? 'กำลังโหลด…' : 'CONNECTING'}</span></header><div class="mp-history-skeleton"><i></i><i></i></div></section></section>`;
  $('#copy-friend-code').addEventListener('click', async () => { await navigator.clipboard?.writeText(code); toast('คัดลอก Friend Code แล้ว'); });
  $('#friend-form').addEventListener('submit', async event => {
    event.preventDefault();
    try { await online.addFriend($('#friend-input').value); toast('เพิ่มเพื่อนแล้ว'); renderFriendsPanel(); }
    catch { toast('ไม่พบ Friend Code นี้'); }
  });
  $('#friend-input').addEventListener('input', event => { event.target.value = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8); });
  if (!online.online) { try { await online.connect(); } catch { $('#friend-list').innerHTML = `<header><div><strong>รายชื่อเพื่อน</strong></div><button id="friend-retry" type="button">ลองใหม่</button></header><div class="mp-empty">${multiplayerIcon('globe')}<div><strong>ออฟไลน์อยู่</strong><small>เกมยังเซฟในเครื่องได้ และลองเชื่อมใหม่ภายหลังได้</small></div></div>`; $('#friend-retry')?.addEventListener('click', renderFriendsPanel); return; } }
  try {
    const result = await online.friends();
    $('#friend-list').innerHTML = `<header><div><strong>รายชื่อเพื่อน</strong><small>${result.friends.length} คนในเครือข่าย</small></div><span>${result.friends.length} FRIENDS</span></header><div>${result.friends.length ? result.friends.map(friend => `<article class="mp-friend-row"><div class="mp-avatar">${playerInitial(friend.playerName)}</div><div><strong>${escapeHtml(friend.playerName)}</strong><small>${escapeHtml(friend.friendCode)} · ${friend.hasBase ? 'ฐานพร้อมให้บุก' : 'ยังไม่มีข้อมูลฐาน'}</small></div><span class="${friend.hasBase ? 'ready' : ''}"><i></i>${friend.hasBase ? 'พร้อม' : 'รอฐาน'}</span><button data-action="raid:${escapeHtml(friend.friendCode)}" type="button" ${friend.hasBase ? '' : 'disabled'}>${multiplayerIcon('swords')} บุก</button></article>`).join('') : `<div class="mp-empty">${multiplayerIcon('friends')}<div><strong>ยังไม่มีเพื่อนในรายชื่อ</strong><small>ส่ง Friend Code ด้านบน หรือเพิ่มโค้ดของเพื่อนคนแรก</small></div></div>`}</div>`;
    bindPanelActions();
  } catch { $('#friend-list').innerHTML = `<header><div><strong>รายชื่อเพื่อน</strong></div><button id="friend-retry" type="button">ลองใหม่</button></header><div class="mp-empty compact"><div><strong>โหลดรายชื่อไม่สำเร็จ</strong><small>ระบบ Multiplayer ยังใช้งานได้ตามปกติ</small></div></div>`; $('#friend-retry')?.addEventListener('click', renderFriendsPanel); }
}

async function renderSettingsPanel() {
  $('#sheet-content').innerHTML = '<div class="section-note">กำลังตรวจคลัง asset สำหรับเว็บ…</div>';
  let catalog;
  try { catalog = await loadAssetCatalog(); } catch { catalog = { summary: { count: 0, bytes: 0, groups: {} } }; }
  const summary = catalog.summary;
  const groupRows = Object.entries(summary.groups || {}).map(([group, count]) => `<span><b>${group}</b><em>${count}</em></span>`).join('');
  const account = auth.user
    ? `<div class="account-status"><strong>✓ Supabase Account เชื่อมแล้ว</strong><small>${escapeHtml(auth.email)}</small><div class="account-actions"><button id="account-sync" type="button">บันทึกตอนนี้</button><button id="account-signout" class="signout" type="button">ออกจากระบบ</button></div></div>`
    : `<div class="account-status"><strong>กำลังเล่นแบบ Guest</strong><small>ข้อมูลอยู่ในเครื่องและ D1 เท่านั้น จนกว่าจะผูกบัญชี</small><div class="account-actions"><button id="account-login" type="button">เข้าสู่ระบบ / สมัคร</button></div></div>`;
  $('#sheet-content').innerHTML = `${account}<label class="settings-row"><span><strong>ชื่อผู้เล่น</strong><small style="display:block;color:#7a817e">แสดงให้เพื่อนเห็น</small></span><input id="name-input" maxlength="20" value="${escapeHtml(state.playerName)}" style="width:170px;border:2px solid #dde4e1;border-radius:14px;padding:8px"></label><div class="settings-row"><span><strong>เสียงและเพลง</strong><small style="display:block;color:#7a817e">Web Audio · ปรับตามอุปกรณ์</small></span><button id="audio-toggle" type="button">${sound.muted ? 'ปิดอยู่' : 'เปิดอยู่'}</button></div><div class="asset-status"><header><strong>Web Asset Library</strong><b>${summary.count} GLB</b></header><p>${(summary.bytes / 1048576).toFixed(1)} MB · Meshopt/WebP · lazy-loaded</p><div>${groupRows}</div></div><div class="settings-row"><span><strong>ระบบเพื่อนออนไลน์</strong><small style="display:block;color:#7a817e">Cloudflare D1 · ${online.online ? 'เชื่อมแล้ว' : 'ออฟไลน์'}</small></span><button data-action="sync" style="border:0;border-radius:14px;padding:8px 14px">ซิงก์</button></div><button id="reset-game" class="danger-button" type="button">เริ่มเกมใหม่และเล่นบทสอนอีกครั้ง</button>`;
  $('.asset-status').insertAdjacentHTML('beforebegin', `<div class="settings-row"><span><strong>คุณภาพกราฟิก</strong><small style="display:block;color:#7a817e">Auto จะลดความละเอียดเมื่อ FPS ตก</small></span><select id="quality-select"><option value="auto">Auto</option><option value="low">ประหยัด</option><option value="medium">สมดุล</option><option value="high">สูง</option></select></div><div class="settings-row"><span><strong>แรงสั่นตอบสนอง</strong><small style="display:block;color:#7a817e">ตอนวางทหาร คริติคอล และใช้สกิล</small></span><button id="haptics-toggle" type="button">${state.preferences.haptics ? 'เปิดอยู่' : 'ปิดอยู่'}</button></div><div class="settings-row"><span><strong>ข้อมูลช่วยพัฒนาเกม</strong><small style="display:block;color:#7a817e">เก็บ event ในเครื่อง ไม่เก็บข้อความหรืออีเมล</small></span><button id="analytics-toggle" type="button">${state.preferences.analytics ? 'อนุญาต' : 'ไม่อนุญาต'}</button></div>`);
  $('#quality-select').value = state.preferences.quality || 'auto';
  $('#quality-select').addEventListener('change', event => { state.preferences.quality = event.target.value; const level = world.setQuality(event.target.value); persist(); toast(`ปรับคุณภาพเป็น ${event.target.options[event.target.selectedIndex].text} · ${level}`); });
  $('#haptics-toggle').addEventListener('click', event => { state.preferences.haptics = !state.preferences.haptics; event.currentTarget.textContent = state.preferences.haptics ? 'เปิดอยู่' : 'ปิดอยู่'; if (state.preferences.haptics) haptic(20); persist(); });
  $('#analytics-toggle').addEventListener('click', event => { state.preferences.analytics = !state.preferences.analytics; telemetry.setEnabled(state.preferences.analytics); event.currentTarget.textContent = state.preferences.analytics ? 'อนุญาต' : 'ไม่อนุญาต'; persist(); });
  $('#name-input').addEventListener('change', event => { state.playerName = event.target.value.trim() || 'Stamp'; persist(); });
  $('#audio-toggle').addEventListener('click', event => { const muted = sound.toggle(); event.currentTarget.textContent = muted ? 'ปิดอยู่' : 'เปิดอยู่'; });
  $('[data-action="sync"]').addEventListener('click', () => Promise.all([online.connect(), auth.user ? auth.save(state) : Promise.resolve()]).then(() => toast('ซิงก์ออนไลน์แล้ว')).catch(() => toast('เชื่อมไม่สำเร็จ')));
  $('#account-login')?.addEventListener('click', showAuthGate);
  $('#account-sync')?.addEventListener('click', () => auth.save(state).then(() => toast('บันทึกลง Supabase แล้ว')).catch(error => toast(friendlyAuthError(error))));
  $('#account-signout')?.addEventListener('click', async () => {
    try {
      await auth.save(state);
      await multiplayer.disconnect();
      await auth.signOut();
      closeSheet();
      showAuthGate();
      toast('ออกจากระบบแล้ว เซฟในเครื่องยังอยู่');
    } catch (error) { toast(friendlyAuthError(error)); }
  });
  $('#reset-game').addEventListener('click', () => {
    if (!window.confirm('เริ่มเมืองใหม่ทั้งหมดหรือไม่?')) return;
    Object.assign(state, resetState()); persist(); closeSheet(); loadCity(true);
  });
}

function bindPanelActions() {
  $('#sheet-content').querySelectorAll('[data-action]').forEach(button => button.addEventListener('click', async () => {
    const [action, value] = button.dataset.action.split(':');
    if (action === 'none') return;
    if (action === 'open-panel') return openSheet(value);
    if (action === 'campaign') {
      if (!armyPower()) return toast('ฝึกกองทัพก่อนออก Campaign');
      const result = beginCampaign(state, value);
      if (!result.ok) return toast(result.reason);
      persist();
      return startBattle({ name: result.stage.title, base: result.stage.base, campaignStageId: result.stage.id, practice: true });
    }
    if (action === 'dungeon') {
      if (!armyPower()) return toast('ฝึกกองทัพก่อนเข้ารอยแยก');
      const dungeon = todayDungeon(state);
      if (dungeon.claimed) return toast('รับรางวัลรอยแยกวันนี้แล้ว');
      return startBattle({ name: dungeon.title, base: dungeon.base, dungeon: true, practice: true });
    }
    if (action === 'research') {
      const result = researchNode(state, value);
      if (!result.ok) return toast(result.reason);
      sound.play('upgrade'); persist(); renderResearchPanel();
      return toast(`${result.node.title} ขึ้นเป็น Lv.${result.level}`);
    }
    if (action === 'equip') {
      const result = equipItem(state, value);
      if (!result.ok) return toast(result.reason);
      sound.play('ui-tap'); persist(); renderArmoryPanel();
      return toast(`ติดตั้ง ${result.item.title} ให้ ${TROOPS[result.item.type].name} แล้ว`);
    }
    if (action === 'replay') {
      const replay = ensureAdventure(state).replays.find(item => item.id === value);
      if (!replay) return toast('ไม่พบ Replay นี้');
      return startBattle({ ...replay.enemy, replay, practice: true, name: `Replay · ${replay.enemy?.name || 'ฐานคู่ต่อสู้'}` });
    }
    if (action === 'build') {
      const result = buildBuilding(state, value, { instant: !state.tutorialComplete });
      if (!result.ok) return toast(result.reason);
      recordProgress(state, 'build');
      sound.play('build');
      triggerScreenFlash();
      closeSheet();
      if (!state.tutorialComplete && ((state.tutorialStep === 1 && value === 'mint') || (state.tutorialStep === 3 && value === 'rally'))) advanceTutorial(state.tutorialStep + 1, false);
      else persist();
      await loadCity(true);
      toast(result.queued ? `เริ่มสร้าง ${BUILDINGS[value].name} แล้ว` : `${BUILDINGS[value].name} สร้างเสร็จแล้ว`);
    }
    if (action === 'save-layout') {
      const result = saveBaseLayout(state, Number(value));
      if (!result.ok) return toast(result.reason);
      persist(); renderBuildPanel(); toast(`บันทึกแปลน ${Number(value) + 1} แล้ว`);
    }
    if (action === 'load-layout') {
      const result = loadBaseLayout(state, Number(value));
      if (!result.ok) return toast(result.reason);
      persist(); closeSheet(); await loadCity(); toast(`ใช้แปลน ${Number(value) + 1} แล้ว`);
    }
    if (action === 'buy-cosmetic') {
      const result = purchaseCosmetic(state, value);
      if (!result.ok) return toast(result.reason);
      persist(); renderStorePanel(); toast(`ปลดล็อก ${result.item.title} แล้ว`);
    }
    if (action === 'buy-product') {
      try {
        button.disabled = true;
        const verification = await billing.purchase(value);
        const result = applyVerifiedPurchase(state, verification);
        if (!result.ok) throw new Error(result.reason);
        persist(); renderStorePanel(); toast(`ได้รับ ${result.product.title} แล้ว`);
      } catch (error) { button.disabled = false; toast(error.message || 'ซื้อสินค้าไม่สำเร็จ'); }
    }
    if (action === 'train') {
      const result = trainTroop(state, value);
      if (!result.ok) return toast(result.reason);
      recordProgress(state, 'train');
      if (!state.tutorialComplete && state.tutorialStep === 5) {
        closeSheet();
        advanceTutorial(6);
      } else {
        persist();
        renderArmyPanel();
      }
      toast(`ฝึก ${combatTroop(state, value)?.name || TROOPS[value].name} แล้ว`);
    }
    if (action === 'expand') {
      const result = expandTerritory(state);
      if (!result.ok) return toast(result.reason);
      sound.play('upgrade');
      closeSheet();
      if (!state.tutorialComplete && state.tutorialStep === 8) advanceTutorial(9, false); else persist();
      await loadCity(true); toast(`ปลดล็อก ${result.expansion.name} แล้ว!`);
    }
    if (action === 'summon') performSummon(Number(value));
    if (action === 'activate') {
      const hero = CHARACTERS[value]; state.activeHeroes[hero.type] = value; persist(); renderRosterPanel(); toast(`เลือก ${hero.name} ลงสนามแล้ว`);
    }
    if (action === 'hero-upgrade') {
      const result = upgradeHero(state, value);
      if (!result.ok) return toast(result.reason);
      persist(); renderRosterPanel(); toast(`${CHARACTERS[value].name} ขึ้นเป็น Lv.${result.level}`);
    }
    if (action === 'claim-mission') {
      const result = claimDailyMission(state, value);
      if (!result.ok) return toast(result.reason);
      sound.play('collect');
      persist();
      renderMissionsPanel();
      showRewardReveal(result.mission.title, result.reward, 'MISSION COMPLETE');
    }
    if (action === 'claim-chest') {
      const result = claimDailyChest(state);
      if (!result.ok) return toast(result.reason);
      sound.play('summon');
      persist();
      renderMissionsPanel();
      showRewardReveal('หีบผู้พิทักษ์ประจำวัน', result.reward, 'DAILY CHEST');
    }
    if (action === 'claim-achievement') {
      const result = claimAchievement(state, value);
      if (!result.ok) return toast(result.reason);
      sound.play('victory');
      persist();
      renderMissionsPanel();
      showRewardReveal(result.achievement.title, result.reward, 'ACHIEVEMENT');
    }
    if (action === 'raid') {
      try { button.disabled = true; button.textContent = 'โหลด…'; const base = await online.friendBase(value); await startBattle({ name: base.ownerName || 'เพื่อน', base }); }
      catch { toast('โหลดฐานเพื่อนไม่สำเร็จ'); button.disabled = false; button.textContent = 'บุก'; }
    }
  }));
}

function performSummon(count) {
  let result;
  if (count === 1 && !state.tutorialComplete && state.tutorialStep === 4) {
    result = { ok: true, results: [summonOne(state, { guaranteedRarity: 'R', characterId: 'crosswind_captain' })] };
    state.army.runner = Math.max(2, state.army.runner);
    pendingTutorialAfterReveal = true;
  } else if (count === 1) result = { ok: true, results: [summonOne(state)] };
  else result = summonMany(state, 10);
  if (!result.ok || result.results.some(item => !item.ok)) return toast(result.reason || result.results.find(item => !item.ok)?.reason);
  recordProgress(state, 'summon', count);
  sound.play('summon');
  persist(); closeSheet(); showSummonResult(result.results);
}

function showSummonResult(results) {
  overlayReturnFocus = document.activeElement;
  const best = results.reduce((max, item) => Math.max(max, RARITIES[item.rarity].rank), 0);
  $('#summon-result-title').textContent = best >= 3 ? 'แสงแห่ง UR ปรากฏ!' : best >= 2 ? 'ได้รับ Super Rare!' : 'ตัวละครตอบรับคำเรียก!';
  $('#summon-reveals').innerHTML = results.map(result => `<article class="reveal-card" style="--rarity:${RARITIES[result.rarity].color}"><span>${result.character.icon}</span><em>${result.rarity}</em><h3>${result.character.name}</h3><p>${result.character.title}</p><small>${result.isNew ? 'NEW!' : `แปลงเป็น ${result.shards} shard`}</small></article>`).join('');
  $('#summon-reveals').classList.toggle('many', results.length > 5);
  $('#summon-result').classList.remove('hidden');
  syncTutorialPresentation();
  window.requestAnimationFrame(() => $('#summon-result-close')?.focus());
}

function escapeHtml(value) { return String(value).replace(/[&<>'"]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char]); }

async function startBattle(enemy = { name: 'กัปตันพายุ', base: null }) {
  replayTimers.forEach(timer => window.clearTimeout(timer));
  replayTimers = [];
  battleReplayMode = Boolean(enemy.replay);
  battleArmy = battleReplayMode ? { ...enemy.replay.army } : state.army;
  const totalTroops = Object.entries(battleArmy).reduce((sum, [type, count]) => sum + (combatTroop(state, type) ? count : 0), 0);
  if (!totalTroops) { openSheet(Object.keys(state.heroes).length ? 'army' : 'summon'); return toast('ต้องมีตัวละครและฝึกนักรบก่อนออกศึก'); }
  battleEnemy = enemy; battleResolved = false; battlePhase = 'scout'; lastBattleMarks = 0; pendingBattleReward = null;
  telemetry.track('battle_started', { source: enemy.campaignStageId ? 'campaign' : enemy.dungeon ? 'dungeon' : enemy.multiplayerUserId ? 'multiplayer' : enemy.replay ? 'replay' : 'raid', enemy: enemy.name || 'Rival' });
  battleAdventure = enemy.campaignStageId ? { type: 'campaign', id: enemy.campaignStageId } : enemy.dungeon ? { type: 'dungeon' } : null;
  battleDeploymentLog = []; battleStartedAt = 0; activeBattleType = '';
  playSceneTransition(); closeSheet(); clearTutorialFocus();
  $('#tutorial').classList.add('hidden'); $('#game-shell').classList.remove('tutorial-visible');
  $('#hud').classList.add('hidden'); $('#battle-hud').classList.remove('hidden'); $('#loading').classList.remove('done');
  $('#loading-text').textContent = 'กองทัพกำลังเดินทางผ่านหมู่เมฆ…'; $('#loading-fill').style.width = '20%';
  await world.showBattle(enemy.base, battleArmy, ratio => { $('#loading-fill').style.width = `${20 + ratio * 75}%`; });
  $('#enemy-name').textContent = enemy.name; $('#loading-fill').style.width = '100%';
  window.setTimeout(() => { $('#loading').classList.add('done'); showCinematic('SKY RAID', `บุกฐาน ${enemy.name}`); }, 220);
  selectedTroop = Object.keys(TROOPS).find(type => battleArmy[type] > 0 && combatTroop(state, type)) || 'runner';
  $('#battle-tip').textContent = battleReplayMode ? 'กำลังเตรียม Replay จากการบุกครั้งก่อน' : `สอดแนม ${GAME_RULES.battle.scoutSeconds} วิ · เวลารบจะเริ่มทันทีเมื่อวางหน่วยแรก`;
  renderTroopDock(); battleDeadline = performance.now() + GAME_RULES.battle.scoutSeconds * 1000; requestAnimationFrame(updateTimer);
  if (battleReplayMode) {
    battlePhase = 'battle';
    battleStartedAt = performance.now();
    battleDeadline = battleStartedAt + GAME_RULES.battle.battleSeconds * 1000;
    $('#battle-tip').textContent = 'REPLAY · กำลังเล่นการวางกำลังอัตโนมัติ';
    for (const deployment of enemy.replay.deployments) {
      replayTimers.push(window.setTimeout(() => deploySelected({ x: deployment.x, z: deployment.z }, deployment.type), Math.min(deployment.at, 90000)));
    }
  }
}

function renderTroopDock() {
  $('#troop-dock').innerHTML = Object.keys(TROOPS).map(type => {
    const hero = combatTroop(state, type); const count = battleArmy[type] || 0;
    return `<button class="troop-button ${type === selectedTroop ? 'selected' : ''}" data-troop="${type}" type="button" ${count <= 0 || !hero ? 'disabled' : ''}><span>${hero?.icon || TROOPS[type].icon}</span><small>${hero ? `${hero.leaderRarity} ${TROOPS[type].name}` : 'LOCK'}</small><b>${count}</b></button>`;
  }).join('');
  $('#troop-dock').querySelectorAll('button').forEach(button => button.addEventListener('click', () => { selectedTroop = button.dataset.troop; renderTroopDock(); $('#battle-tip').textContent = `${combatTroop(state, selectedTroop)?.name} พร้อมแล้ว — แตะขอบเกาะเพื่อวาง`; }));
}

async function deploySelected(point, forcedType = '') {
  const type = forcedType || selectedTroop;
  if (battleResolved || !type || battleArmy[type] <= 0) return;
  const hero = combatTroop(state, type);
  if (!hero) return;
  if (battlePhase === 'scout') {
    battlePhase = 'battle';
    battleStartedAt = performance.now();
    battleDeadline = performance.now() + GAME_RULES.battle.battleSeconds * 1000;
    $('#battle-tip').textContent = 'เริ่มรบแล้ว · ใช้ Sky Current สีฟ้าเพื่อเร่งความเร็ว';
  }
  const deployed = await world.deployTroop(type, point, hero);
  if (!deployed) return;
  haptic(12);
  activeBattleType = type;
  battleArmy[type] -= 1;
  if (!battleReplayMode) {
    battleDeploymentLog.push({ type, x: point.x, z: point.z, at: Math.max(0, performance.now() - (battleStartedAt || performance.now())) });
    persist(false);
  }
  renderTroopDock();
  $('#hero-skill-name').textContent = type === 'smith' ? 'Aegis of Storm' : type === 'lotus' ? 'Lotus Renewal' : type === 'ember' ? 'Emberfall' : 'Sky Volley';
  $('#hero-skill-button').disabled = false;
  $('#hero-skill-cooldown').textContent = 'พร้อมใช้';
  if (battleArmy[type] <= 0) selectedTroop = Object.keys(TROOPS).find(key => battleArmy[key] > 0 && combatTroop(state, key)) || selectedTroop;
}

function renderBattleUpdate(update) {
  $('#destruction').textContent = `${update.percent}%`;
  $('#marks').textContent = [0,1,2].map(index => index < update.marks ? '★' : '☆').join(' ');
  $('#stamp-charge').textContent = `${update.charge}%`; $('#stamp-button').disabled = update.charge < 100;
  const skillButton = $('#hero-skill-button');
  skillButton.disabled = !activeBattleType || update.skillReadyIn > 0;
  $('#hero-skill-cooldown').textContent = !activeBattleType ? 'วางหัวหน้ากอง' : update.skillReadyIn > 0 ? `${update.skillReadyIn} วิ` : 'พร้อมใช้';
  if (update.marks > lastBattleMarks) {
    lastBattleMarks = update.marks;
    triggerScreenFlash();
  }
}

function updateTimer(now) {
  if (battleResolved || world.mode !== 'battle') return;
  const seconds = Math.max(0, Math.ceil((battleDeadline - now) / 1000));
  $('#battle-time').textContent = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2,'0')}`;
  if (seconds <= 0 && battlePhase === 'scout') {
    battlePhase = 'battle';
    battleDeadline = performance.now() + GAME_RULES.battle.battleSeconds * 1000;
    $('#battle-tip').textContent = 'หมดเวลาสอดแนม · เลือกนักรบแล้วแตะขอบเกาะเพื่อวางกำลัง';
    requestAnimationFrame(updateTimer);
    return;
  }
  if (seconds <= 0) { const battle = world.battle; const hallAlive = battle.targets.some(target => target.userData.state.type === 'hall' && target.userData.alive); finishBattle({ percent: battle.percent, marks: (battle.percent >= 50 ? 1 : 0) + (!hallAlive ? 1 : 0) + (battle.percent >= 100 ? 1 : 0) }); return; }
  requestAnimationFrame(updateTimer);
}

function finishBattle(result) {
  if (battleResolved) return;
  battleResolved = true; world.endBattle();
  replayTimers.forEach(timer => window.clearTimeout(timer));
  replayTimers = [];
  telemetry.track('battle_finished', { percent: result.percent, marks: result.marks, replay: battleReplayMode, source: battleAdventure?.type || 'raid' });
  let reward = { gold: 0, wood: 0, dew: 0, multiplier: 1 };
  let progressionReward = null;
  if (!battleReplayMode) {
    reward = applyBattleReward(state, result.percent, result.marks, {
      opponentId: battleEnemy?.multiplayerUserId || battleEnemy?.base?.ownerCode || battleEnemy?.name,
      opponentName: battleEnemy?.name,
      practice: Boolean(battleEnemy?.practice)
    });
    if (battleAdventure?.type === 'campaign') progressionReward = completeCampaign(state, battleAdventure.id, result.marks, result.percent);
    if (battleAdventure?.type === 'dungeon' && result.marks > 0) progressionReward = completeDungeon(state, result.marks);
    recordProgress(state, 'battle');
    if (result.marks > 0) recordProgress(state, 'win');
    storeReplay(state, {
      enemy: { name: battleEnemy?.name || 'Rival', base: battleEnemy?.base || null, practice: true },
      army: Object.fromEntries(Object.keys(TROOPS).map(type => [type, (battleArmy[type] || 0) + battleDeploymentLog.filter(item => item.type === type).length])),
      deployments: battleDeploymentLog, percent: result.percent, marks: result.marks
    });
    persist();
  }
  sound.play(result.marks > 0 ? 'victory' : 'defeat');
  triggerScreenFlash(result.marks <= 0);
  $('#battle-hud').classList.add('hidden'); $('#result').classList.remove('hidden');
  $('#result-title').textContent = battleReplayMode ? 'จบ Replay' : result.marks > 0 ? battleAdventure?.type === 'campaign' ? 'ผ่านด่าน!' : 'ชัยชนะ!' : 'การสำรวจเสร็จสิ้น';
  $('#result-marks').textContent = [0,1,2].map(index => index < result.marks ? '★' : '☆').join(' ');
  $('#result-percent').textContent = `${result.percent}%`;
  const extra = progressionReward?.reward || {};
  const totalReward = { gold: reward.gold + (extra.gold || 0), wood: reward.wood + (extra.wood || 0), dew: reward.dew + (extra.dew || 0), research: extra.research || 0, equipment: progressionReward?.equipment?.title || '' };
  $('#result-reward').textContent = battleReplayMode ? 'Replay ไม่ใช้ทหารและไม่ให้รางวัล' : `รับ ${rewardText(totalReward) || 'ประสบการณ์การรบ'}${reward.multiplier < 1 ? ` · รางวัลฝึกซ้อม ×${reward.multiplier}` : ''}`;
  $('#return-home').textContent = !battleReplayMode && result.marks > 0 ? 'เปิดหีบรางวัล' : 'กลับหมู่บ้าน';
  pendingBattleReward = !battleReplayMode && result.marks > 0 ? totalReward : null;
  if (!battleReplayMode) online.recordBattle(battleEnemy?.name || 'Rival', result.percent, result.marks).catch(() => {});
  if (!battleReplayMode && battleEnemy?.multiplayerUserId) {
    const opponent = { userId: battleEnemy.multiplayerUserId, name: battleEnemy.name, roomCode: battleEnemy.roomCode };
    multiplayer.recordMatch(opponent, result).catch(() => {});
    multiplayer.sendResult(result).catch(() => {});
  }
}

function surrender() {
  if (!world.battle || battleResolved) return;
  const battle = world.battle; const hallAlive = battle.targets.some(target => target.userData.state.type === 'hall' && target.userData.alive);
  finishBattle({ percent: battle.percent, marks: (battle.percent >= 50 ? 1 : 0) + (!hallAlive ? 1 : 0) + (battle.percent >= 100 ? 1 : 0) });
}

function tutorialOverlayOpen() {
  return ['#auth-gate', '#reward-reveal', '#summon-result', '#result']
    .some(selector => !$(selector)?.classList.contains('hidden'));
}

function setTutorialVisibility(element, visible) {
  element.classList.toggle('hidden', !visible);
  element.setAttribute('aria-hidden', String(!visible));
}

function syncTutorialPresentation() {
  const available = !state.tutorialComplete && world.mode === 'city';
  const sheetVisible = Boolean(activePanel) && !$('#sheet').classList.contains('hidden');
  const blocked = tutorialOverlayOpen();
  const step = TUTORIAL[state.tutorialStep] || TUTORIAL[0];
  const guide = TUTORIAL_GUIDES[state.tutorialStep];
  const stepNumber = Math.min(TUTORIAL.length, state.tutorialStep + 1);
  const showCard = available && !sheetVisible && !tutorialGuided && !blocked;
  const showSheetGuide = available && sheetVisible && tutorialGuided && !blocked;
  const showHudGuide = available && !sheetVisible && tutorialGuided && !blocked;

  setTutorialVisibility($('#tutorial'), showCard);
  setTutorialVisibility($('#tutorial-sheet-guide'), showSheetGuide);
  setTutorialVisibility($('#tutorial-hud-guide'), showHudGuide);
  $('#sheet').classList.toggle('tutorial-guide-visible', showSheetGuide);
  $('#game-shell').classList.toggle('tutorial-visible', showCard);
  $('#game-shell').classList.toggle('tutorial-guided', showSheetGuide || showHudGuide);

  if (showSheetGuide) {
    $('#tutorial-sheet-step').textContent = `LUMI GUIDE · ${stepNumber}/${TUTORIAL.length}`;
    $('#tutorial-sheet-title').textContent = step.title;
    $('#tutorial-sheet-text').textContent = guide?.text || step.text;
  }
  if (showHudGuide) {
    $('#tutorial-hud-step').textContent = `LUMI GUIDE · ${stepNumber}/${TUTORIAL.length}`;
    $('#tutorial-hud-title').textContent = step.title;
    $('#tutorial-hud-text').textContent = guide?.text || step.text;
  }
  if (!showCard && !showSheetGuide && !showHudGuide) clearTutorialFocus();
}

function showTutorial() {
  clearTutorialFocus();
  if (state.tutorialComplete || world.mode !== 'city') {
    syncTutorialPresentation();
    return;
  }
  const step = TUTORIAL[state.tutorialStep] || TUTORIAL[0];
  const stepNumber = Math.min(TUTORIAL.length, state.tutorialStep + 1);
  $('#tutorial-title').textContent = step.title;
  $('#tutorial-text').textContent = step.text;
  $('#tutorial-reward').textContent = step.reward || '';
  $('#tutorial-step-label').textContent = `${stepNumber} / ${TUTORIAL.length}`;
  $('#tutorial-progress').style.width = `${stepNumber / TUTORIAL.length * 100}%`;
  $('#tutorial-action').textContent = step.action;
  $('#tutorial-skip').classList.toggle('hidden', state.tutorialStep >= 8);
  syncTutorialPresentation();
  if (!tutorialGuided && !activePanel && !tutorialOverlayOpen() && step.focus) focusSoon(step.focus);
}

function focusSoon(selector) {
  window.clearTimeout(tutorialFocusTimer);
  tutorialFocusTimer = window.setTimeout(() => {
    tutorialFocusTimer = 0;
    document.querySelectorAll('.tutorial-focus').forEach(element => element.classList.remove('tutorial-focus'));
    const target = document.querySelector(selector);
    target?.classList.add('tutorial-focus');
    if (tutorialGuided && target?.closest('.sheet-content')) {
      target.scrollIntoView({ block: 'center', inline: 'nearest', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
  }, 80);
}

function clearTutorialFocus() {
  window.clearTimeout(tutorialFocusTimer);
  tutorialFocusTimer = 0;
  document.querySelectorAll('.tutorial-focus').forEach(element => element.classList.remove('tutorial-focus'));
}

function advanceTutorial(next, show = true) {
  const completedStep = state.tutorialStep;
  grantTutorialReward(state, completedStep);
  state.tutorialStep = next;
  tutorialGuided = false;
  persist();
  if (show) showTutorial();
}

function tutorialAction() {
  const step = state.tutorialStep;
  if (step === 0) { advanceTutorial(1, false); tutorialGuided = true; openSheet('build'); }
  else if (step === 1 || step === 3) { tutorialGuided = true; openSheet('build'); }
  else if (step === 2) collect();
  else if (step === 4) { tutorialGuided = true; openSheet('summon'); }
  else if (step === 5) { tutorialGuided = true; openSheet('army'); }
  else if (step === 6) startBattle();
  else if (step === 7) {
    closeSheet();
    tutorialGuided = true;
    syncTutorialPresentation();
    const hallRoot = world.worldRoot.children.find(child => child.userData?.state?.type === 'hall');
    if (hallRoot) { world.selectBuilding(hallRoot); selectBuilding(hallRoot.userData.state); focusSoon('.upgrade-button'); }
  } else if (step === 8) { tutorialGuided = true; openSheet('territory'); }
  else if (step === 9) { state.tutorialComplete = true; tutorialGuided = false; persist(); showTutorial(); toast('บทสอนจบแล้ว — เมืองนี้เป็นของคุณ!'); }
}

function skipTutorial() {
  state.tutorialComplete = true; state.tutorialStep = 9;
  tutorialGuided = false;
  state.gold += 2400; state.spiritWood += 1800; state.skyDew += 180; state.summonTickets += 2;
  if (!Object.keys(state.heroes).length) { summonOne(state, { free: true, guaranteedRarity: 'R', characterId: 'crosswind_captain' }); state.army.runner = 3; }
  persist(); showTutorial(); toast('ข้ามบทสอนแล้ว รับชุดเริ่มต้นเข้าคลัง');
}

document.querySelectorAll('[data-panel]').forEach(button => button.addEventListener('click', () => openSheet(button.dataset.panel)));
$('#sheet-close').addEventListener('click', closeSheet);
$('#sheet-backdrop').addEventListener('click', closeSheet);
$('#collect-button').addEventListener('click', collect);
$('#battle-button').addEventListener('click', () => startBattle());
$('#stamp-button').addEventListener('click', () => { if (world.useStamp()) { sound.play('stamp'); haptic([22,35,28]); toast('Gale Stamp! กองทัพเคลื่อนที่เร็วขึ้น 6 วินาที'); } });
$('#hero-skill-button').addEventListener('click', () => {
  if (!activeBattleType || !world.useHeroSkill(activeBattleType)) return;
  sound.play('stamp');
  haptic([18,30,32]);
  $('#hero-skill-button').disabled = true;
  $('#hero-skill-cooldown').textContent = '22 วิ';
});
$('#surrender-button').addEventListener('click', surrender);
$('#return-home').addEventListener('click', () => {
  if (!state.tutorialComplete && state.tutorialStep === 6) advanceTutorial(7, false);
  const reward = pendingBattleReward;
  pendingBattleReward = null;
  playSceneTransition();
  loadCity(true).then(() => {
    if (reward) showRewardReveal('หีบชัยชนะ', reward, 'BATTLE REWARD');
  });
});
$('#summon-result-close').addEventListener('click', () => {
  $('#summon-result').classList.add('hidden');
  if (pendingTutorialAfterReveal) {
    pendingTutorialAfterReveal = false;
    advanceTutorial(5, false);
    tutorialGuided = true;
    openSheet('army');
  }
  else openSheet('roster');
});
$('#tutorial-action').addEventListener('click', tutorialAction);
$('#tutorial-skip').addEventListener('click', skipTutorial);
$('#next-action').addEventListener('click', event => {
  const button = event.currentTarget;
  if (button.disabled || button.classList.contains('hidden')) return;
  const { panel, action } = button.dataset;
  button.disabled = true;
  if (panel) openSheet(panel);
  else if (action === 'collect') collect();
  else if (action === 'battle') startBattle();
  else {
    showTutorial();
    window.setTimeout(() => { if (!button.classList.contains('hidden')) button.disabled = false; }, 250);
  }
});
$('#reward-close').addEventListener('click', () => {
  window.clearTimeout(rewardTimer);
  $('#reward-reveal').classList.add('hidden');
  renderObjectiveUI();
  showTutorial();
  const target = overlayReturnFocus;
  overlayReturnFocus = null;
  window.requestAnimationFrame(() => target?.isConnected && target.focus());
});
$('#profile-button').addEventListener('click', () => openSheet('settings'));
document.querySelectorAll('[data-resource]').forEach(button => button.addEventListener('click', collect));
window.addEventListener('pointerdown', () => sound.unlock(), { once: true });
window.addEventListener('error', event => telemetry.captureError(event.error || event.message, 'window'));
window.addEventListener('unhandledrejection', event => telemetry.captureError(event.reason, 'promise'));
window.addEventListener('keydown', event => {
  const visibleModal = ['#reward-reveal', '#summon-result', '#result', '#auth-gate', '#sheet']
    .map(selector => $(selector)).find(element => element && !element.classList.contains('hidden'));
  if (event.key === 'Tab' && visibleModal) {
    const focusable = [...visibleModal.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter(element => element.offsetParent !== null);
    if (focusable.length) {
      const first = focusable[0]; const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    return;
  }
  if (event.key !== 'Escape') return;
  if (!$('#reward-reveal').classList.contains('hidden')) $('#reward-close').click();
  else if (!$('#summon-result').classList.contains('hidden')) $('#summon-result-close').click();
  else if (!$('#sheet').classList.contains('hidden')) closeSheet();
});
document.querySelectorAll('[data-auth-mode]').forEach(button => button.addEventListener('click', () => setAuthMode(button.dataset.authMode)));
$('#auth-guest').addEventListener('click', () => {
  localStorage.setItem('clash-of-stamp-guest', '1');
  $('#auth-gate').classList.add('hidden');
  showTutorial();
  toast('เล่นแบบ Guest ได้เลย — ล็อกอินภายหลังได้จากโปรไฟล์');
});
$('#auth-form').addEventListener('submit', async event => {
  event.preventDefault();
  const email = $('#auth-email').value.trim();
  const password = $('#auth-password').value;
  const submit = $('#auth-submit');
  submit.disabled = true;
  submit.textContent = authMode === 'signup' ? 'กำลังสร้างบัญชี…' : 'กำลังโหลดเกาะ…';
  try {
    if (authMode === 'recovery') {
      await auth.updatePassword(password);
    } else if (authMode === 'signup') {
      const result = await auth.signUp(email, password, state.playerName);
      if (!result.session) {
        authMessage('ส่งอีเมลยืนยันแล้ว กดลิงก์ในอีเมลแล้วกลับมาเข้าสู่ระบบ');
        return;
      }
    } else {
      await auth.signIn(email, password);
    }
    await attachAccount(true);
    $('#auth-gate').classList.add('hidden');
    showTutorial();
    online.connect().catch(() => {});
    multiplayer.connectLobby().then(joinRoomFromInviteUrl).catch(() => {});
    toast(authMode === 'recovery' ? 'ตั้งรหัสผ่านใหม่แล้ว' : authMode === 'signup' ? 'สร้างบัญชีและบันทึกเมืองแล้ว' : 'เข้าสู่ระบบและโหลดเมืองแล้ว');
  } catch (error) {
    authMessage(friendlyAuthError(error), true);
  } finally {
    submit.disabled = false;
    submit.textContent = authMode === 'recovery' ? 'บันทึกรหัสผ่านใหม่' : authMode === 'signup' ? 'สร้างบัญชีและบันทึกเมือง' : 'เข้าสู่เกาะ';
  }
});
$('#auth-forgot').addEventListener('click', async () => {
  const email = $('#auth-email').value.trim();
  if (!email) return authMessage('กรอกอีเมลก่อนขอเปลี่ยนรหัสผ่าน', true);
  try {
    await auth.resetPassword(email);
    authMessage('ส่งลิงก์ตั้งรหัสผ่านใหม่แล้ว กรุณาตรวจอีเมล');
  } catch (error) { authMessage(friendlyAuthError(error), true); }
});

setInterval(() => {
  const completed = processConstruction(state);
  if (completed.length) {
    saveState(state);
    auth.queueSave(state);
    renderResources();
    if (world.mode === 'city') loadCity().then(() => toast(`${completed.map(item => BUILDINGS[item.type].name).join(', ')} สร้างเสร็จแล้ว`));
  } else renderResources();
}, 5000);
async function boot() {
  try {
    await auth.init();
    if (auth.user) await attachAccount(false);
  } catch (error) {
    console.warn('Supabase Auth unavailable; continuing as guest.', error);
  }
  if (auth.recoveryMode) { showAuthGate(); setAuthMode('recovery'); }
  else if (!auth.user && localStorage.getItem('clash-of-stamp-guest') !== '1') showAuthGate();
  await loadCity(true);
  online.connect().catch(() => $('#online-dot').classList.remove('online'));
  if (auth.user) multiplayer.connectLobby().then(joinRoomFromInviteUrl).catch(() => {});
}

boot();
if ('serviceWorker' in navigator && import.meta.env.PROD) navigator.serviceWorker.register('/sw.js').catch(() => {});
